<template>
  <div class="voice-report-confirm">
    <!-- 顶部导航栏 -->
    <!-- 小程序环境下使用小程序原生导航栏的返回，隐藏 H5 自绘的返回按钮避免重复 -->
    <div :class="['navbar', isInMiniProgram ? 'navbar-miniprogram' : '']">
      <div class="navbar-back" @click="goBack" v-if="!isInMiniProgram">
        <div class="back-arrow"></div>
      </div>
      <div class="navbar-title">{{ isEditMode ? '上报单确认' : '上报单详情' }}</div>
      <div class="navbar-placeholder" v-if="!isInMiniProgram"></div>
    </div>

    <!-- 内容区域 -->
    <div class="content-scroll">
      <!-- 提示横幅（仅编辑模式） -->
      <div class="tip-banner" v-if="isEditMode">
        <div class="tip-icon">✏️</div>
        <div class="tip-text">小图也会存在疏漏的情况，您可根据实际情况编辑修正上报内容哦！</div>
      </div>

      <!-- 表单卡片 -->
      <div class="form-card">
        <!-- 类型选择 -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-label">类型</span>
            <span class="section-hint">选择事件类型</span>
          </div>
          <div class="tag-group">
            <div
              v-for="(item, index) in eventTypes"
              :key="index"
              :class="['tag-btn', formData.selectedTypeIndex === index ? 'tag-active' : '', !isEditMode ? 'tag-disabled' : '']"
              @click="selectEventType(index)"
            >
              <span class="tag-emoji">{{ item.emoji }}</span>
              <span class="tag-label">{{ item.label }}</span>
            </div>
          </div>
        </div>

        <!-- 地点信息 -->
        <div class="form-section">
          <span class="section-label">地点</span>
          <div class="location-map" @click="onLocationClick">
            <div class="mapBanner">
              <img v-show="!mapState.userSelectPoint" :src="mapImg" alt="" />
              <div v-if="!mapState.userSelectPoint && !isEditMode" class="map-overlay-text">点击查看地图位置</div>
              <div v-show="mapState.userSelectPoint" id="voiceMapContainer" class="map-container"></div>
            </div>
            <p class="map-label">地图位置{{ formData.detailAddress ? `：${formData.detailAddress}` : '' }}</p>
          </div>
        </div>

        <!-- 时间选择 -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-label">时间</span>
            <span class="section-hint">填写发生时间</span>
          </div>
          <div class="time-display" @click="onTimeClick">
            <span class="time-icon">📅</span>
            <span class="time-text">{{ formData.reportTime || '请选择时间' }}</span>
          </div>
        </div>

        <!-- 影响程度 -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-label">影响（备选项）</span>
            <span class="section-hint">路况与程度</span>
          </div>
          <div class="tag-group">
            <div
              v-for="(item, index) in impactLevels"
              :key="index"
              :class="['tag-btn', 'impact-tag', formData.impactLevel === item.value ? `impact-active-${item.color}` : '', !isEditMode ? 'tag-disabled' : '']"
              @click="selectImpact(item.value)"
            >
              <span class="tag-emoji">{{ item.emoji }}</span>
              <span class="tag-label">{{ item.label }}</span>
            </div>
          </div>
        </div>

        <!-- 受影响车道 -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-label">受影响车道（多选）</span>
          </div>
          <div class="lane-picker" @click="onLaneClick">
            <span class="lane-text">{{ lanesDisplayText }}</span>
            <div class="lane-arrow"></div>
          </div>
        </div>
      </div>

      <!-- 媒体附件 -->
      <div class="form-card media-card">
        <div class="section-header">
          <span class="section-label">📎 媒体附件（备选项）</span>
        </div>
        <div class="media-upload-area">
          <!-- 已上传的图片/视频列表 -->
          <template v-for="(item, index) in locationPics" :key="index">
            <div class="upload-item" v-if="item.src">
              <div class="upload-item-inner">
                <img v-if="item.mediaType !== 'video'" crossorigin="anonymous" :src="item.src" @click="onPreviewImage(index)" />
                <video v-else :src="item.src" class="upload-video-thumb" @click="onPreviewVideo(item.src)"></video>
                <div class="upload-play" v-if="item.mediaType === 'video'">▶</div>
                <div class="upload-delete" v-if="isEditMode" @click="uploadDelete(index)">✕</div>
              </div>
              <span class="upload-label">{{ item.mediaType === 'video' ? '视频' : '图片' }}{{ index + 1 }}</span>
            </div>
          </template>
          <!-- 上传按钮 -->
          <!--
            三分支策略：
            1) APP 端 + easyearn 拍摄桥可用（如 APP 原生 WebView 或新版 h5page 壳）：
               走原生桥，模仿 messageAppearDetailH5.vue（"道路新增"）的 clack()。
            2) APP 端 + easyearn 拍摄桥不可用（当前 APP 老版本 h5page 壳精简桥环境）：
               展示灰色降级提示（媒体附件本就标注为"备选项"，不影响主流程）；
               已对接 APP 同学补齐桥能力，新版 APP 后会自动切回分支 1。
            3) 小程序 web-view / 普通 H5：渲染 input[type=file]，由用户手势直接命中
               input 触发原生选择器（外层 div 不绑 @click，避免 WebView 拦截）。
          -->
          <template v-if="isInApp && appHasMediaBridge">
            <!-- 分支 1：APP 桥完整 -->
            <div class="upload-add" v-if="isEditMode" @click="onAppPickImage">
              <div class="upload-add-inner">
                <img src="../assets/image_message_appear/camera.png" alt="" />
              </div>
              <span class="upload-label">拍照/图片</span>
            </div>
            <div class="upload-add" v-if="isEditMode" @click="onAppPickVideo">
              <div class="upload-add-inner upload-video-add">
                <span class="video-icon">🎥</span>
              </div>
              <span class="upload-label">录像/视频</span>
            </div>
          </template>
          <template v-else-if="isInApp && !appHasMediaBridge">
            <!-- 分支 2：APP 精简桥环境 → 降级提示 -->
            <div class="media-fallback-tip" v-if="isEditMode">
              当前 APP 版本暂不支持在此页上传附件，可升级 APP 后再使用，或先保存草稿后到小程序补充附件。
            </div>
          </template>
          <template v-else>
            <!-- 分支 3：小程序 / 普通 H5 -->
            <div class="upload-add" v-if="isEditMode">
              <div class="upload-add-inner">
                <img src="../assets/image_message_appear/camera.png" alt="" />
                <input
                  type="file"
                  accept="image/*"
                  ref="imageFileInput"
                  class="upload-file-input"
                  @change="onImageFileChange"
                />
              </div>
              <span class="upload-label">拍照/图片</span>
            </div>
            <div class="upload-add" v-if="isEditMode">
              <div class="upload-add-inner upload-video-add">
                <span class="video-icon">🎥</span>
                <input
                  type="file"
                  accept="video/*"
                  ref="videoFileInput"
                  class="upload-file-input"
                  @change="onVideoFileChange"
                />
              </div>
              <span class="upload-label">录像/视频</span>
            </div>
          </template>
        </div>
        <div v-if="!isEditMode && locationPics.length === 0" class="no-media-tip">暂无附件</div>
      </div>

      <!-- 图片预览 -->
      <previewImage
        v-if="previewObj.showPreviewImg"
        :showPreviewImg="previewObj.showPreviewImg"
        :photoList="previewObj.imageList"
        :initPage="previewObj.initPage"
        :isViewExample="false"
        @setShowPreviewImg="val => previewObj.showPreviewImg = val"
      />

      <!-- 上传 Loading -->
      <nut-popup :style="{ padding: '30px 50px', backgroundColor: 'transparent' }" v-model:visible="uploading">
        <nut-icon name="loading" size="30" color="#fff"></nut-icon>
      </nut-popup>

      <!-- 底部操作区 -->
      <div class="bottom-actions" v-if="isEditMode">
        <div :class="['submit-btn', submitting ? 'disabled' : '']" @click="submitReport(40)">
          <span class="submit-icon">📤</span>
          <span>提交</span>
        </div>
        <div :class="['draft-btn', submitting ? 'disabled' : '']" @click="submitReport(20)">
          <span>保存草稿</span>
        </div>
      </div>
      <div class="bottom-actions" v-else>
        <div class="submit-btn" @click="goBack">
          <span>返回</span>
        </div>
      </div>

      <!-- 底部提示 -->
      <!-- <div class="bottom-tip">
        提交后将同步至腾讯地图平台，您可在"历史"中查看进度与反馈。
      </div> -->

      <div class="safe-bottom"></div>
    </div>

    <!-- 时间选择器 -->
    <t-popup v-model="timePickerVisible" placement="bottom">
      <t-date-time-picker
        v-model="timePickerValue"
        :class="store.state.isH5 ? isIOS ? 't_date_time_picker_self newcss' : 't_date_time_picker_self newcss5' : 't_date_time_picker_self'"
        mode="minute"
        title="选择时间"
        format="YYYY-MM-DD HH:mm"
        @confirm="onTimeConfirm"
        @cancel="timePickerVisible = false"
      />
    </t-popup>

    <!-- 车道选择弹窗 -->
    <nut-popup v-model:visible="lanePickerVisible" position="bottom" round>
      <div class="lane-popup">
        <div class="lane-popup-header">
          <span class="lane-popup-cancel" @click="lanePickerVisible = false">取消</span>
          <span class="lane-popup-title">选择受影响车道</span>
          <span class="lane-popup-confirm" @click="confirmLanes">确定</span>
        </div>
        <div class="lane-popup-body">
          <div
            v-for="(item, index) in laneOptions"
            :key="index"
            :class="['lane-option', tempSelectedLanes.includes(item.value) ? 'lane-selected' : '']"
            @click="toggleLane(item.value)"
          >
            <span>{{ item.label }}</span>
            <span class="lane-check" v-if="tempSelectedLanes.includes(item.value)">✓</span>
          </div>
        </div>
      </div>
    </nut-popup>

    <!-- APP 无原生 picLocation 时的 H5 地图选点兜底 -->
    <div v-if="appLocationPickerVisible" class="app-location-picker">
      <div class="app-location-header">
        <span class="app-location-cancel" @click="closeAppH5LocationPicker">取消</span>
        <span class="app-location-title">位置确认</span>
        <span class="app-location-confirm" @click="confirmAppH5LocationPicker">确定</span>
      </div>
      <div class="app-location-tip">拖动地图，将标记移动到事件发生位置</div>
      <div class="app-location-map-wrap">
        <div id="appH5LocationMap" class="app-location-map"></div>
        <div class="app-location-marker"></div>
      </div>
      <div class="app-location-address">{{ appLocationPickerAddress || '移动地图后点击确定' }}</div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onBeforeMount, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { Notify } from "@nutui/nutui";
import request from "@/utils/request";
import { aiVoiceInfoReport, getCosCredentialsByScene } from "@/utils/api";
import { setMiniImg } from '@/utils/utils';
import { mapImg } from './config';
import previewImage from "../components/previewImage";
import wx from "weixin-js-sdk";

const store = useStore();
const route = useRoute();
const router = useRouter();
const getEasyearn = () => window.easyearn;
const isInApp = computed(() => !!getEasyearn());
// APP 端 h5page 壳容器的 easyearn 桥分两种：
//   - 完整版（如"道路新增"页所在容器）：含 takePicture / takePoiPicture / takeBatchPoiVideo
//     等拍摄能力 → 走 APP 原生桥
//   - 精简版（当前语音上报确认页所在 h5page 壳容器）：只有 8 个基础方法
//     （getAccessToken / getOpenId / getVersionCode / goBackToPage / updateToken 等），
//     没有任何拍摄桥 → 暂时无法在该容器内调起拍照/相册
//     已对接 APP 同学补桥；新版 APP 上线后，本判定会自动切回桥调用路径，前端无需改动。
const appHasMediaBridge = computed(() => {
  const ee = getEasyearn();
  if (!ee) return false;
  // 主桥：takePicture（与"道路新增"使用同一桥）；备用桥：takePoiPicture
  return typeof ee.takePicture === 'function' || typeof ee.takePoiPicture === 'function';
});
const isIOS = !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
// 拍照/视频上传完全对齐 src/components/photo.vue 在线上多端验证过的实现：
//   - 模板中渲染 input[type=file]，由用户手势直接命中 input 触发原生选择器；
//   - APP h5page WebView 对 file input 的拦截极苛刻：父元素若拦截 click 事件，
//     即使最终 JS 调 input.click()，WebView 也会判定"非用户直接交互"而忽略；
//   - 所以模板里【外层 div 不绑 @click】，纯靠 input 自然点击。
// 注：原本想走 easyearn.takePicture/takePoiPicture/takeBatchPoiVideo 桥，但日志确认
//     当前 APP 版本中这三个方法不存在（直接抛 TypeError），故统一走 input 链路。

// 判断当前是否运行在小程序（微信/QQ）web-view 环境内
// 仅当【UA 含 miniprogram】且【不存在 window.easyearn】时才认为是小程序环境。
// 注意：
//   - type=h5page / store.state.isH5 不能作为判据：
//     APP 端通过 easyearn 打开 H5 壳页时也会携带 type=h5page，main.vue 会把
//     store.state.isH5 设置为 true，若以此判断会把 APP 环境误判为小程序，
//     进而隐藏掉顶部自绘返回按钮，造成 APP 内无法返回。
//   - easyearn 仅在 APP 宿主中注入，小程序 web-view 内不存在，可作为强排他条件。
const ua = (navigator.userAgent || '').toLowerCase();
const isInMiniProgram = ua.indexOf('miniprogram') !== -1 && !window.easyearn;

// edit=1 为编辑模式；从地图选点返回时 URL 可能没有 edit 参数，才从 localStorage 兜底。
// 注意：URL 显式传了 edit（无论是 '0' 还是 '1'）时，必须严格以 URL 为准，
// 否则会被上一次编辑流程写入的 localStorage 污染（例如：编辑草稿 → 选点写入 '1'
// → 未清理 → 再从历史页"查看详情"非草稿时，URL edit=0 却被 localStorage 覆盖为 '1'，
// 导致查看模式下错误显示"提交/保存草稿"按钮而不是"返回"按钮）。
//
// 真机兼容（手机小程序 web-view）：vue-router 的 query 在 iframe 重载 / 选点回跳等
// 场景可能丢失，因此额外支持从以下兜底源判断：
//   1) route.query.edit ('0'/'1')                —— Vue Router 内部跳转主路径
//   2) route.query.mode ('edit'/'view')          —— 同上冗余一份
//   3) window.location.hash (#mode=edit|view)    —— hash 跨 web-view 重载最稳定
//   4) localStorage 'voice_report_edit_mode'     —— 仅当以上都没命中时
// 优先级从高到低依次兜底。
function parseModeFromHash() {
  try {
    const h = typeof window !== 'undefined' ? (window.location.hash || '') : '';
    const m = h.match(/[#&]mode=(edit|view)/);
    return m ? m[1] : '';
  } catch (e) {
    return '';
  }
}
const isEditMode = computed(() => {
  const qEdit = route.query.edit;
  if (qEdit === '1') return true;
  if (qEdit === '0') return false;

  const qMode = route.query.mode;
  if (qMode === 'edit') return true;
  if (qMode === 'view') return false;

  const hashMode = parseModeFromHash();
  if (hashMode === 'edit') return true;
  if (hashMode === 'view') return false;

  return localStorage.getItem('voice_report_edit_mode') === '1';
});

// 事件类型配置
// 注意：这里必须用 reactive 而非 const 数组字面量。
// 场景：后端/AI 返回的事件类型可能超出下面这 6 项预设（例如"水浸"等），
// 此时需要在运行时动态 push 一个新选项，否则会被 findIndex 兜底成 index=0（事故处置），
// 导致查看/编辑页显示的事件类型与后端不一致。
// reactive 数组 push 后，模板里的 v-for="(item, index) in eventTypes" 会自动渲染新项。
const eventTypes = reactive([
  { emoji: '🚗', label: '事故处置', key: 'accident_disposal', aliases: ['事故'] },
  { emoji: '🚧', label: '占路施工', key: 'road_occupation_construction', aliases: ['施工', '道路施工', '占道施工'] },
  { emoji: '🌧️', label: '天气预警', key: 'weather_warning', aliases: ['天气', '暴雨', '积水', '结冰', '大雾', '团雾', '冰雪'] },
  { emoji: '🚫', label: '通行阻碍', key: 'traffic_obstruction', aliases: ['拥堵', '路障', '障碍', '通行受阻', '阻碍'] },
  { emoji: '🚨', label: '安全管控', key: 'safety_control', aliases: ['封路', '管控', '交通管制', '限行'] },
  { emoji: '📝', label: '其他', key: 'other', aliases: [] },
]);

/**
 * 确保给定的事件类型字符串存在于 eventTypes 中，返回其 index。
 * - 预设列表中「完全匹配 label」或「label 被 typeStr 包含」都视为命中；
 * - 旧版/同义类型通过 aliases 映射到新六类；
 * - 未命中则动态追加一项，emoji 统一用 📝（自定义），label 取 typeStr 原文；
 * - typeStr 为空时返回 -1，让调用方决定兜底策略。
 */
function ensureEventType(typeStr) {
  const s = typeStr === undefined || typeStr === null ? '' : String(typeStr).trim();
  if (!s) return -1;
  // 1) 精确、包含或同义词匹配
  const idx = eventTypes.findIndex(t => {
    if (t.label === s || s.includes(t.label)) return true;
    return (t.aliases || []).some(alias => s === alias || s.includes(alias));
  });
  if (idx >= 0) return idx;
  // 2) 运行时追加自定义类型
  eventTypes.push({
    emoji: '📝',
    label: s,
    key: `custom_${Date.now()}`,
    custom: true,
  });
  return eventTypes.length - 1;
}

// 影响程度配置
const impactLevels = [
  { emoji: '✅', label: '轻微', value: 3, color: 'green' },
  { emoji: '⚠️', label: '中等', value: 2, color: 'orange' },
  { emoji: '🔴', label: '严重', value: 1, color: 'red' },
];

// 车道选项
const laneOptions = [
  { label: '左车道', value: 1 },
  { label: '中间车道', value: 2 },
  { label: '右车道', value: 3 },
  { label: '应急车道', value: 4 },
  { label: '所有车道', value: 5 },
  { label: '未知', value: 6 },
];

/**
 * 统一解析 lanesAffected，兼容多种来源格式：
 * 1) 数字数组    [1, 2]                → AI 语音识别链路（voiceReport.js）
 * 2) 数字串数组  ["1", "2"]            → 某些后端序列化格式
 * 3) label 数组  ["左车道", "中间车道"] → 后端回显 / COS JSON
 * 4) 字符串      "1,2" / "左车道、右车道" → 历史数据兜底
 * 返回值：number[]，对应 laneOptions 的 value 集合。
 */
function parseLanesAffected(input) {
  if (input === undefined || input === null || input === '') return [];
  const arr = Array.isArray(input) ? input : String(input).split(/[,、]/);
  const values = arr.map(item => {
    if (item === undefined || item === null || item === '') return undefined;
    // label 字符串（'未知' 按业务语义不选中任何车道）
    if (typeof item === 'string' && isNaN(Number(item))) {
      if (item === '未知') return undefined;
      return laneOptions.find(o => o.label === item)?.value;
    }
    // 数字或数字字符串：按 value 校验
    const num = Number(item);
    return laneOptions.some(o => o.value === num) ? num : undefined;
  }).filter(v => v !== undefined);
  return values;
}

// 表单数据
const formData = reactive({
  selectedTypeIndex: 0,
  eventTypeStr: '',
  detailAddress: '',
  reportTime: '',
  impactLevel: 1,
  selectedLanes: [],
  briefDescription: '',
  originalText: '',
  reportName: '',
  orderId: '',
});

// 原始数据（用于检测是否手动修改）
const originalFormData = ref(null);
// 完整原始 AI 返回数据
const rawData = ref(null);
// 是否存在真实的 AI 原始识别快照（仅"语音识别后首次进入确认页"为 true）。
// 注意：不能用 isEditMode 判断——首次新建场景 URL 也是 edit=1，isEditMode 为 true，
// 但此时后端需要 originalAiAnswer 来保存 AI 原始结果；而从历史页编辑已有单时，
// rawData 是上次提交的值（不是 AI 原始答案），再回传会污染后端保存的快照。
// 唯一可靠的判据：入参里是否带 _rawData 包裹（见 voiceReport.js confirmReport 的 reportData 结构）。
const hasRawAiAnswer = ref(false);

const submitting = ref(false);
const timePickerVisible = ref(false);
const timePickerValue = ref(new Date().toLocaleString());
const lanePickerVisible = ref(false);
const tempSelectedLanes = ref([]);
const voiceReportSessionId = ref('');
const appLocationPickerVisible = ref(false);
const appLocationPickerAddress = ref('');
let appLocationPickerMap = null;

// 车道展示文本
const lanesDisplayText = computed(() => {
  if (formData.selectedLanes.length === 0) return '请选择受影响车道';
  return formData.selectedLanes
    .map(v => laneOptions.find(o => o.value === v)?.label)
    .filter(Boolean)
    .join('、');
});

// 从 URL hash 中解析参数
function parseHash() {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) return {};
  const params = {};
  hash.substring(1).split('&').forEach(part => {
    const [key, ...rest] = part.split('=');
    if (key) params[key] = rest.join('=');
  });
  return params;
}

function parseReportDataPayload(value, source) {
  const raw = normalizeQueryValue(value);
  if (!raw) return null;
  const candidates = [raw];
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded && decoded !== raw) candidates.push(decoded);
  } catch (e) {
    console.warn(`${source} reportData decode 失败，尝试按原文解析`, e);
  }
  let lastErr = null;
  for (const item of candidates) {
    try {
      return JSON.parse(item);
    } catch (e) {
      lastErr = e;
    }
  }
  console.error(`从 ${source} 解析 reportData 失败`, lastErr);
  return null;
}

function parseReportDataFromLocationSearch() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    return parseReportDataPayload(params.get('reportData'), 'location.search');
  } catch (e) {
    console.error('从 location.search 解析 reportData 失败', e);
    return null;
  }
}

function readVoiceReportPickSnapshot() {
  try {
    const raw = localStorage.getItem('voice_report_form_data');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('[选点回跳] 解析持久化数据失败:', e);
    return null;
  }
}

function clearVoiceReportPickSnapshot() {
  localStorage.removeItem('voice_report_form_data');
  localStorage.removeItem('voice_report_picking_location');
}

// 解析初始数据（优先级：URL hash → URL query → location.search → localStorage）
onBeforeMount(() => {
  // 语音上报链路：小程序 h5webNoNav 拼 URL 时会把 openId 放在 query 里，
  // main.vue 的 onBeforeMount 虽然也会执行，但执行时机不确定，
  // 这里再做一次兜底写入，确保本页面能拿到 userOpenId。
  const queryOpenId = normalizeOpenId(route.query.openId || route.query.openid || route.query.opid);
  if (queryOpenId && !normalizeOpenId(store.state.userOpenId)) {
    store.commit('setuserOpenId', queryOpenId);
    try { localStorage.setItem('userOpenId', queryOpenId); } catch (e) { /* ignore */ }
  }

  // ★ 关键修复：选点回跳场景下不再执行 initFormData ★
  // 背景：小程序 h5webNoNav 在"选点回跳"时复用了首次进入时构造的 iframeUrl（含
  // `#reportData=...` hash），导致 confirm 页 iframe 被重新加载、onBeforeMount 又
  // 走 hash 分支再调一次 initFormData。这次 initFormData 会：
  //   1) 重置 formData（用户已修改的字段被回退到 AI/历史原值）；
  //   2) 调 loadMediaFromUrlList / loadMediaFromCosUrl 再次往 locationPics push 媒体，
  //      虽然 upsertLocationPic 通过 srcCopy 去重，但 loadMediaFromCosUrl 是异步分支，
  //      其回调可能晚于 restoreFromStorage 的 splice 替换，把"刚被快照恢复的"媒体
  //      之外的项再 push 一遍，表现为"附件无缘无故增加"。
  // 修复策略：只有当 picking 标志和快照同时存在（即上一步确实是跳到选点页）时，
  // 本次进入的状态才交给 onMounted 里的 restoreFromStorage 恢复。
  // 若只有 picking 标志但快照不存在，说明是上一次选点/异常退出留下的脏状态，必须清掉并继续 initFormData，
  // 否则从新的入口进入本页会被误判为选点回跳，页面数据全部空白。
  const isPickingBack = (() => {
    try { return localStorage.getItem('voice_report_picking_location') === '1'; }
    catch (e) { return false; }
  })();
  const pickSnapshot = isPickingBack ? readVoiceReportPickSnapshot() : null;
  if (isPickingBack && pickSnapshot) {
    console.log('[选点回跳] onBeforeMount 检测到有效 picking 快照，跳过 initFormData，等待 restoreFromStorage 恢复');
    return;
  }
  if (isPickingBack) {
    console.warn('[选点回跳] 检测到残留 picking 标志但无有效快照，清理后按正常入口初始化');
    clearVoiceReportPickSnapshot();
  }

  let reportData = null;

  // 1. 从 URL hash 解析（小程序 web-view 通过 hash 传递大数据）
  const hashParams = parseHash();
  if (hashParams.reportData) {
    reportData = parseReportDataPayload(hashParams.reportData, 'URL hash');
    if (reportData) console.log('从 URL hash 解析 reportData 成功');
  }

  // 2. 从 URL query 解析（hash 在部分小程序 web-view 链路中会偶发丢失，query 作为兜底）
  if (!reportData && route.query.reportData) {
    reportData = parseReportDataPayload(route.query.reportData, 'URL query');
    if (reportData) console.log('从 URL query 解析 reportData 成功');
  }

  // 3. 从 location.search 再兜底一次，避免 router query 初始化异常时漏取
  if (!reportData) {
    reportData = parseReportDataFromLocationSearch();
    if (reportData) console.log('从 location.search 解析 reportData 成功');
  }

  // 4. 从 localStorage 兜底（App 环境或其他场景）
  if (!reportData) {
    try {
      const stored = localStorage.getItem('voice_report_data');
      if (stored) {
        reportData = JSON.parse(stored);
        console.log('从 localStorage 解析 reportData 成功');
      }
    } catch (e) {
      console.error('从 localStorage 解析 reportData 失败', e);
    }
  }

  if (reportData) {
    const reportOpenId = normalizeOpenId(reportData.openId || reportData.userOpenId || (reportData._rawData && (reportData._rawData.openId || reportData._rawData.openid)));
    if (reportOpenId && !normalizeOpenId(store.state.userOpenId)) {
      cacheUserOpenId(reportOpenId);
    }
    initFormData(reportData);
  } else if (route.query.hasReportData === '1' || route.query.hasReportData === 1) {
    // 小程序侧明确挂过 reportData（voiceReport.js confirmReport 时写入 storage 并带了 hasReportData=1 标记），
    // 但这里 hash / query / localStorage 三级兜底全部拿不到 —— 一般是 hash 在某些跳转链路中被丢弃。
    // 此时不做静默，给用户一个明确提示，避免停留在空白页；同时允许用户返回重新生成上报单。
    console.warn('voiceReportConfirm: 标记显示应有 reportData，但三级兜底均未取到，疑似 hash 丢失');
    nextTick(() => {
      // eslint-disable-next-line no-undef
      if (typeof wx !== 'undefined' && wx.miniProgram && wx.miniProgram.navigateBack) {
        // 在小程序 web-view 环境下，直接把用户退回上一页，由用户重新触发核对
        try {
          // 先用 alert 告知，再返回；alert 在小程序 webview 中可用
          window.alert && window.alert('上报单数据加载失败，请返回重新生成');
        } catch (_) { /* ignore */ }
        wx.miniProgram.navigateBack({ delta: 1 });
      } else {
        try {
          window.alert && window.alert('上报单数据加载失败，请返回重新生成');
        } catch (_) { /* ignore */ }
      }
    });
  }
});

/**
 * 兜底补齐 userOpenId
 * 场景：小程序 WebView 多次跳转链路下，URL 里未必带 openId，
 * 导致 store.state.userOpenId 为空，进而 COS 凭证接口拿不到 openid，
 * 凭证下发为空 → SDK 匿名上传 → 403 AccessDenied。
 */
function normalizeOpenId(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeOpenId).find(Boolean) || '';
  }
  if (value === undefined || value === null) return '';
  const v = String(value).trim();
  return v && v !== 'undefined' && v !== 'null' ? v : '';
}

function pickOpenIdFromSearchParams(params) {
  const KEYS = ['openId', 'openid', 'opid', 'OpenId'];
  for (const k of KEYS) {
    const values = params.getAll(k);
    const openId = values.map(normalizeOpenId).find(Boolean);
    if (openId) return openId;
  }
  return '';
}

function pickOpenIdFromLocation() {
  try {
    const q = new URLSearchParams(window.location.search || '');
    const fromSearch = pickOpenIdFromSearchParams(q);
    if (fromSearch) return fromSearch;

    const h = window.location.hash || '';
    const i = h.indexOf('?');
    if (i > -1) {
      const hq = new URLSearchParams(h.slice(i + 1));
      const fromHashQuery = pickOpenIdFromSearchParams(hq);
      if (fromHashQuery) return fromHashQuery;
    }
    // 兼容 hash 写法 #openId=xxx 或 #mode=xxx&openId=xxx
    if (h) {
      const body = h.replace(/^#/, '');
      const hq2 = new URLSearchParams(body);
      const fromHash = pickOpenIdFromSearchParams(hq2);
      if (fromHash) return fromHash;
    }
  } catch (e) { /* ignore */ }
  return '';
}

function cacheUserOpenId(openId) {
  const normalized = normalizeOpenId(openId);
  if (!normalized) return '';
  store.commit('setuserOpenId', normalized);
  try { localStorage.setItem('userOpenId', normalized); } catch (e) { /* ignore */ }
  try { sessionStorage.setItem('userOpenId', normalized); } catch (e) { /* ignore */ }
  return normalized;
}

function ensureUserOpenId() {
  const storeOpenId = normalizeOpenId(store.state.userOpenId);
  if (storeOpenId) return storeOpenId;
  let openId = '';
  // 1) vue-router route.query（支持多种大小写变体；重复参数时取第一个有效值）
  try {
    if (route && route.query) {
      openId = normalizeOpenId(route.query.openId || route.query.openid || route.query.opid || route.query.OpenId);
    }
  } catch (e) { /* ignore */ }
  // 2) window.location 兜底（search/hash 双通道）
  if (!openId) openId = pickOpenIdFromLocation();
  // 3) 原生宿主注入的 easyearn.getOpenId()
  if (!openId && window.easyearn && typeof window.easyearn.getOpenId === 'function') {
    try { openId = normalizeOpenId(window.easyearn.getOpenId()); } catch (e) { /* ignore */ }
  }
  // 4) store.state.userInfo（格式 'openid/xxx'）
  if (!openId && store.state.userInfo && typeof store.state.userInfo === 'string') {
    openId = normalizeOpenId(store.state.userInfo.split('/')[0]);
  }
  // 5) localStorage / sessionStorage 兜底（上次缓存 / 选点快照）
  if (!openId) {
    try { openId = normalizeOpenId(localStorage.getItem('userOpenId')); } catch (e) { /* ignore */ }
  }
  if (!openId) {
    try { openId = normalizeOpenId(sessionStorage.getItem('userOpenId')); } catch (e) { /* ignore */ }
  }
  if (openId) {
    const cachedOpenId = cacheUserOpenId(openId);
    console.log('[COS排查][ensureUserOpenId] 兜底写入:', cachedOpenId);
    return cachedOpenId;
  }
  console.warn('[COS排查][ensureUserOpenId] 仍未获取到 openId，route=', route && route.query,
    ' location.search=', window.location.search, ' hash=', window.location.hash,
    ' userInfo=', store.state.userInfo);
  return '';
}

onMounted(() => {
  console.log(store.state, 'voiceReportConfirm store.state')
  ensureUserOpenId();

  // 位置选好后 Android/iOS 端通知回调 JS 端
  window.onLocationPicked = (lng, lat) => {
    console.log("onLocationPicked 回调", lng, lat);
    mapState.userSelectedLngLat = { lng, lat };
    mapState.userSelectPoint = true;
    // 同步刷新已有媒体坐标（与选点回跳 reportSearchWX 分支同理）
    if (lat && lng) {
      locationPics.forEach(pic => { pic.lat = lat; pic.lng = lng; });
    }
    // 逆地址解析获取地址描述
    reverseGeocode(lat, lng);
    // 等待 v-show DOM 更新后再初始化地图，否则容器尺寸为 0 地图渲染空白
    nextTick(() => {
      initMap();
    });
  };

  // APP 原生拍摄回调：对齐 placeadd/locationDetail.vue 的 takePoiPicture / takeBatchPoiVideo 链路
  window.onRecivePoiPicPath = handleNativeImageCallback;
  window.onRecivePicPath = handleNativeImageCallback;
  window.shootVideoCallback = handleNativeVideoCallback;

  navigator.geolocation.getCurrentPosition((position) => {
    const { latitude, longitude } = position.coords;
    console.log(latitude, longitude, 'voiceReport coords');
    mapState.pointLngLat = { lat: latitude, lng: longitude };
    // 如果还没有选过点，用当前位置初始化
    if (!mapState.userSelectedLngLat.lat) {
      mapState.userSelectedLngLat = { lat: latitude, lng: longitude };
    }
    restoreFromStorage();
  }, err => {
    console.log(err, 'geolocation err');
    restoreFromStorage();
  }, {
    enableHighAccuracy: true,
    maximumAge: 0
  });

  // 媒体上传：APP 端走 easyearn 原生拍摄回调；小程序 web-view / 普通 H5 走 input[type=file]。
  // 这套分支与 placeadd/locationDetail.vue + components/photo.vue 保持一致。
});

function initFormData(data) {
  // _rawData：来自语音识别后回填的原始 AI 答案（首次进入）
  // 历史入口（voiceReportHistory viewDetail → localStorage.voice_report_data）
  // 存的是接口列表 item 本身，不存在 _rawData 包裹，此时直接用 data 兜底，
  // 否则后续提交取 raw.orderId / raw.id 等字段都是 undefined，导致编辑提交
  // 被当作新建单。
  hasRawAiAnswer.value = !!data._rawData;
  rawData.value = data._rawData || data || {};

  // 匹配事件类型：若预设列表没有则动态追加，避免"水浸"等自定义类型被误映射成"事故处置"
  const typeStr = data.type || data.eventTypeStr || '';
  const typeIndex = ensureEventType(typeStr);
  formData.selectedTypeIndex = typeIndex >= 0 ? typeIndex : 0;
  formData.eventTypeStr = typeIndex >= 0 ? eventTypes[typeIndex].label : typeStr;

  formData.detailAddress = data.locationDetail || data.detailAddress || '';
  formData.reportTime = data.time || data.reportTime || '';
  formData.impactLevel = data.impactLevel || 1;
  formData.briefDescription = data.briefDescription || '';
  formData.originalText = data.originalText || '';
  formData.reportName = data.title || data.reportName || '交通路况上报单';
  formData.orderId = getOrderIdFromData(data) || getOrderIdFromData(rawData.value); // 编辑时需要回传 orderId 以区分是修改还是新建

  // 解析受影响车道（兼容 number/string/array 格式）
  // - AI 语音识别链路（voiceReport.js）回填的是数字数组：[1, 2]
  // - 历史/编辑回显链路（后端）回填的是 label 字符串数组：["左车道", "中间车道"]
  // - 兜底也可能是 "1,2" / "左车道、右车道" 等单字符串
  if (data.lanesAffected) {
    formData.selectedLanes = parseLanesAffected(data.lanesAffected);
  }

  // 解析经纬度（从接口字段或图片坐标中获取）
  // ★ 关键修复：当 data.cosUrl 存在时，不从 data.lat/data.lng 设置 mapState。
  // 背景：后端列表接口返回的 item.lat/item.lng 可能是 common.loc_x/loc_y（用户设备位置），
  // 而不是用户在地图上选择的事件地点坐标。COS JSON 里的顶层 lat/lng 是提交时从
  // mapState.userSelectedLngLat 写入的（即用户选点坐标），是权威来源。
  // 如果 cosUrl 存在 → 坐标将由 loadMediaFromCosUrl 异步从 COS JSON 回填；
  // 如果 cosUrl 不存在（如语音识别首次进入）→ 走 data.lat/data.lng 兜底。
  const hasCosUrl = !!(data.cosUrl);
  if (!hasCosUrl) {
    const lat = data.lat || data.latitude || '';
    const lng = data.lng || data.longitude || '';
    if (lat && lng) {
      mapState.userSelectedLngLat = { lat, lng };
    }
    // 从 eventLocation 字段解析（格式可能是 "lat,lng"）
    if (!lat && data.eventLocation) {
      const parts = data.eventLocation.split(',');
      if (parts.length === 2) {
        mapState.userSelectedLngLat = { lat: parts[0], lng: parts[1] };
      }
    }
  }

  // ★ 幂等防护：每次 initFormData 都先清空已有的媒体列表 ★
  // 背景：选点回跳场景下，h5webNoNav 可能复用首次进入时的 iframeUrl（含 reportData hash），
  // 导致本函数被重复调用。如果不清空就直接 push，叠加到 restoreFromStorage 之后异步回调
  // 还能再追加，最终表现为"附件列表里出现重复 / 莫名增加"。
  // 清空是安全的：选点回跳场景下用户原有的 locationPics 会被随后的 restoreFromStorage 从快照
  // 完整恢复，所以这里清空不会丢失数据。
  locationPics.splice(0, locationPics.length);

  // 解析图片/视频列表（来自 item.url 数组或 COS JSON 的 url 字段）
  if (data.url && Array.isArray(data.url) && data.url.length > 0) {
    loadMediaFromUrlList(data.url);
    // 如果图片有坐标且尚未设置地图坐标，从第一张图片中提取
    if (!mapState.userSelectedLngLat.lat) {
      const firstWithCoord = data.url.find(item => item.lat && item.lng);
      if (firstWithCoord) {
        mapState.userSelectedLngLat = { lat: firstWithCoord.lat, lng: firstWithCoord.lng };
      }
    }
  } else if (data.cosUrl) {
    // 无论查看模式还是编辑模式，只要没有 data.url，就从 cosUrl 下载 COS JSON 拉取媒体数据
    loadMediaFromCosUrl(data.cosUrl);
  }

  // 只要恢复出了有效坐标，无论查看/编辑模式都自动渲染地图。
  if (mapState.userSelectedLngLat.lat && mapState.userSelectedLngLat.lng) {
    mapState.userSelectPoint = true;
    nextTick(() => {
      initMap();
    });
  } else if (!hasCosUrl && formData.detailAddress) {
    // 有地名但无坐标且没有 cosUrl 时，通过正向地理编码解析坐标后渲染地图。
    // 注意：有 cosUrl 时不走此分支 —— COS JSON 异步回来后会带回精确坐标并自行渲染地图。
    // forwardGeocode 解析精度有限（"XXX附近"类文本会落到 POI 中心点而非用户选的精确坐标），
    // 若先于 COS JSON 渲染会导致地图位置偏移（用户反馈"confirm 页地图位置和选点页不一致"）。
    forwardGeocode(formData.detailAddress);
  }

  // 保存原始表单值用于检测修改
  originalFormData.value = {
    selectedTypeIndex: formData.selectedTypeIndex,
    detailAddress: formData.detailAddress,
    reportTime: formData.reportTime,
    impactLevel: formData.impactLevel,
    selectedLanes: [...formData.selectedLanes],
    briefDescription: formData.briefDescription,
    reportName: formData.reportName,
  };
}

/**
 * 从 url 数组加载图片/视频到 locationPics（数据已内嵌在 item 中）
 */
function loadMediaFromUrlList(urlList) {
  urlList.forEach((item, index) => {
    if (!item.src) return;
    const isVideo = item.mediaType === 'video' || item.src.match(/\.(mp4|mov|avi|wmv|flv|webm)(\?|$)/i);
    const newItem = {
      id: index,
      src: '',
      srcCopy: item.src,
      lat: item.lat || '',
      lng: item.lng || '',
      mediaType: isVideo ? 'video' : 'image',
    };
    const picIndex = upsertLocationPic(newItem);
    // 获取预签名 URL 用于展示
    getPresignedUrl([item.src], picIndex);
  });
}

/**
 * 从 cosUrl 下载 COS JSON，解析其中的图片/视频列表
 * 参考 policeReportPage 的 checkDetailReqCos → getCosJSON
 */
function loadMediaFromCosUrl(cosUrl) {
  console.log('[COS排查][confirm] 入参原始 cosUrl =', cosUrl);
  // 历史数据中有些 cosUrl 以 http:// 开头，真机（iOS ATS）和后端签名接口都要求 https，
  // 否则会返回 40404 "请确认是否配置bucket"。这里统一规范为 https://。
  let url = cosUrl;
  if (/^http:\/\//i.test(url)) {
    url = url.replace(/^http:\/\//i, 'https://');
  } else if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }
  console.log('[COS排查][confirm] 规范化后 url =', url);
  if (!window.getReadOnlyPresignedUrlApi) {
    console.warn('getReadOnlyPresignedUrlApi 未就绪');
    return;
  }
  window.getReadOnlyPresignedUrlApi(url).then(signedUrl => {
    console.log('[COS排查][confirm] 签名成功 signedUrl =', signedUrl);
    request("get", signedUrl).then(res => {
      console.log('COS JSON 数据:', res);
      // 回填媒体列表
      if (res.url && Array.isArray(res.url) && res.url.length > 0) {
        loadMediaFromUrlList(res.url);
      }
      // 如果 COS JSON 中有字段，补充回填（避免覆盖已有值）
      if (res.reportName && !formData.reportName) formData.reportName = res.reportName;
      if (res.detailAddress && !formData.detailAddress) formData.detailAddress = res.detailAddress;
      if (res.reportTime && !formData.reportTime) formData.reportTime = res.reportTime;
      if (res.eventTypeStr && !formData.eventTypeStr) {
        // 预设外的类型动态追加；旧版类型通过 aliases 归一到新六类。
        const idx = ensureEventType(res.eventTypeStr);
        if (idx >= 0) {
          formData.selectedTypeIndex = idx;
          formData.eventTypeStr = eventTypes[idx].label;
        } else {
          formData.eventTypeStr = res.eventTypeStr;
        }
      }
      if (res.impactLevel && formData.impactLevel === 1) formData.impactLevel = res.impactLevel;
      if (res.briefDescription && !formData.briefDescription) formData.briefDescription = res.briefDescription;
      if (res.originalText && !formData.originalText) formData.originalText = res.originalText;
      if (res.lanesAffected && formData.selectedLanes.length === 0) {
        formData.selectedLanes = parseLanesAffected(res.lanesAffected);
      }
      // 从 COS JSON 回填经纬度（优先级：JSON 顶层 lat/lng > eventLocation > 图片坐标）。
      // ★ COS JSON 是提交时由前端写入的权威事件坐标源 ★
      // 总是用 COS JSON 坐标覆盖 mapState（不再以 !mapState.lat 为前提），原因：
      //   - 后端列表接口返回的 item.lat 可能是 common.loc_x（设备位置），不可信；
      //   - onMounted 里的 geolocation 可能先于 COS JSON 回调写入了"用户当前位置"，
      //     如果保留 if(!lat) 判断，COS JSON 的正确坐标就永远进不来。
      if (res.lat && res.lng) {
        mapState.userSelectedLngLat = { lat: res.lat, lng: res.lng };
      } else if (res.eventLocation && typeof res.eventLocation === 'string') {
        const parts = res.eventLocation.split(',');
        if (parts.length === 2 && parts[0] && parts[1]) {
          mapState.userSelectedLngLat = { lat: parts[0], lng: parts[1] };
        }
      } else if (!mapState.userSelectedLngLat.lat && res.url && Array.isArray(res.url)) {
        // 图片坐标作为末级兜底，仅在前面两级都没有时才使用
        const firstWithCoord = res.url.find(item => item.lat && item.lng);
        if (firstWithCoord) {
          mapState.userSelectedLngLat = { lat: firstWithCoord.lat, lng: firstWithCoord.lng };
        }
      }
      // 拿到坐标后，查看/编辑模式都自动渲染地图（编辑模式下用户也希望直接看到地图位置）
      if (mapState.userSelectedLngLat.lat && mapState.userSelectedLngLat.lng) {
        mapState.userSelectPoint = true;
        nextTick(() => { initMap(); });
      }
    }).catch(err => {
      console.error('[COS排查][confirm] 下载 COS JSON 失败', { url, err });
    });
  }).catch(err => {
    console.error('[COS排查][confirm] 获取 COS JSON 预签名 URL 失败', { url, err });
  });
}

// 检测是否手动修改
function checkManuallyEdited() {
  if (!originalFormData.value) return 1;
  const orig = originalFormData.value;
  if (formData.selectedTypeIndex !== orig.selectedTypeIndex) return 1;
  if (formData.detailAddress !== orig.detailAddress) return 1;
  if (formData.reportTime !== orig.reportTime) return 1;
  if (formData.impactLevel !== orig.impactLevel) return 1;
  if (formData.reportName !== orig.reportName) return 1;
  if (formData.briefDescription !== orig.briefDescription) return 1;
  if (JSON.stringify(formData.selectedLanes) !== JSON.stringify(orig.selectedLanes)) return 1;
  return 0;
}

// 选择事件类型
function selectEventType(index) {
  if (!isEditMode.value) return;
  formData.selectedTypeIndex = index;
  formData.eventTypeStr = eventTypes[index].label;
}

// 选择影响程度
function selectImpact(value) {
  if (!isEditMode.value) return;
  formData.impactLevel = value;
}

// 点击"地点"（地图区域）：
// - 查看模式：只渲染只读地图（无坐标兜底用 detailAddress 解析坐标）；
// - 编辑模式：直接跳选点页修改位置（行为与用户预期一致）。
//
// 三个修复要点：
// 1) 跳选点前传有效坐标（没有就传空串，由选点页兜底）：
//    - 小程序选点页 H5PicLocation.js 拿到 latitude=undefined → 落回用户当前位置
//      （语音上报链路下这正是我们期望的兜底行为）；
//    - APP easyearn.picLocation 拿到空 lng/lat 会拒绝弹出选点页，所以 APP 分支
//      额外用 getFallbackLngLat() 兜底一个本地/默认坐标。
// 2) 跳选点前必须把 formData + locationPics 一起持久化，回跳后恢复。
//    locationPics 之前没存，导致回来后图片/视频丢失（"问题2"根因之一）。
// 3) 编辑模式下，无论当前是否已有地址，点击地图都直接跳选点；
//    之前的逻辑会先在原地渲染只读地图，导致用户感觉"点了没反应"。
function onLocationClick() {
  // 查看模式：进入小程序原生地图页只读查看（userCheckDetail=true，不允许拖动/确认编辑）
  if (!isEditMode.value) {
    if (mapState.userSelectedLngLat.lat && mapState.userSelectedLngLat.lng) {
      openReadOnlyLocationMap();
      return;
    }
    // 有地名但无坐标 → 先正向解析，成功后进入只读地图页
    if (formData.detailAddress) {
      forwardGeocode(formData.detailAddress, () => { openReadOnlyLocationMap(); });
      return;
    }
    Notify.warn('暂无位置信息');
    return;
  }

  // 编辑模式：直接跳选点（没坐标就让选点页用用户当前位置兜底）
  goPickLocationWithEnsureLngLat();
}

/**
 * 编辑模式下跳转选点：
 * 1) 已有坐标 → 直接跳，选点页以该坐标为中心；
 * 2) 没坐标 → 直接跳，选点页内部 wx.getLocation 会以"用户当前位置"为中心。
 *
 * 说明：
 * - 之前这里会在没坐标时对 formData.detailAddress 做正向地理编码（geocodeAddress）
 *   来补坐标，考虑到：
 *     a) 用户只要选过一次点，mapState.userSelectedLngLat 就稳定有值（当次会话由
 *        restoreFromStorage 用 reportSearchWX 覆盖，跨会话由后端 item 的 lat/lng /
 *        cosUrl JSON 回填），不会再进入"补坐标"分支；
 *     b) 语音识别后未提交就退出的场景，AI 给的 detailAddress 精度有限，即使解析
 *        出来也不一定是用户真实位置，反而让用户多一步"先看到一个错位置再挪动"；
 *     c) geocode 是外部 JSONP 调用，会增加一次网络往返，耗时且可能失败。
 * - 因此直接移除 geocode 补坐标这条兜底，没坐标时交给选点页的 wx.getLocation 兜底即可。
 */
function goPickLocationWithEnsureLngLat() {
  const hasValidLngLat = !!(mapState.userSelectedLngLat.lat && mapState.userSelectedLngLat.lng);
  if (hasValidLngLat) {
    console.log('[选点] 已有坐标，直接跳选点页', mapState.userSelectedLngLat);
    onPicLocation();
    return;
  }
  console.log('[选点] 无坐标，跳选点页由选点页用用户当前位置兜底', {
    detailAddress: formData.detailAddress,
  });
  onPicLocation();
}

// 点击时间（编辑模式保护）
function onTimeClick() {
  if (!isEditMode.value) return;
  showTimePicker();
}

// 点击车道（编辑模式保护）
function onLaneClick() {
  if (!isEditMode.value) return;
  showLanePicker();
}

// 地图相关状态
const mapState = reactive({
  userSelectPoint: false,
  userSelectedLngLat: { lng: '', lat: '' },
  pointLngLat: { lng: '', lat: '' },
  locationWayName: '',
  datayime: true,
});
// TMap 在 initMap 中动态从 window 获取，避免 SDK 异步加载时取不到

// 点击地图区域 → 跳转选点
// APP 以 easyearn 为强判据；APP WebView 中即使存在 wx.miniProgram 对象，也不能误走小程序页面栈。
function detectPickLocationEnv() {
  const ee = getEasyearn();
  const hasEasyearn = !!ee;
  const hasPicLocation = hasEasyearn && typeof ee.picLocation === 'function';
  const hasWxMini = typeof wx !== 'undefined' && wx && wx.miniProgram
    && typeof wx.miniProgram.navigateTo === 'function';
  const uaHit = ua.indexOf('miniprogram') !== -1;

  if (hasEasyearn) return hasPicLocation ? 'app' : 'app-h5-fallback';
  if (uaHit || hasWxMini) return 'miniprogram';
  return 'h5';
}

function onPicLocation() {
  const env = detectPickLocationEnv();
  const ee = getEasyearn();
  console.log('[选点] detectPickLocationEnv =', env, {
    ua,
    hasWxMini: typeof wx !== 'undefined' && !!(wx && wx.miniProgram),
    hasEasyearn: !!ee,
    hasPicLocation: !!(ee && typeof ee.picLocation === 'function'),
  });

  if (env === 'miniprogram') {
    // 小程序环境 → 跳转小程序原生选点页
    if (mapState.datayime) {
      goWeixinPicLocation();
      mapState.datayime = false;
      setTimeout(() => { mapState.datayime = true }, 3000);
    }
    return;
  }

  if (env === 'app') {
    // 把 lng/lat 转成"非空字符串"，APP 桥对 undefined/null/空串非常敏感
    const fallback = getFallbackLngLat();
    const lng = String(mapState.userSelectedLngLat.lng || mapState.pointLngLat.lng || fallback.lng);
    const lat = String(mapState.userSelectedLngLat.lat || mapState.pointLngLat.lat || fallback.lat);
    console.log('[选点][APP] easyearn.picLocation 调用', { lng, lat });
    saveFormStateForPickLocation();
    try {
      ee.picLocation(lng, lat, false, false, '200', true);
    } catch (err) {
      console.error('[选点][APP] easyearn.picLocation 调用异常，降级 H5 选点:', err);
      openAppH5LocationPicker();
    }
    return;
  }

  if (env === 'app-h5-fallback') {
    console.warn('[选点][APP] easyearn 已注入但 picLocation 不可用，使用 H5 地图兜底', {
      easyearnKeys: Object.keys(ee || {})
    });
    openAppH5LocationPicker();
    return;
  }

  // env === 'h5'：既不是小程序也不是 APP，当前页面没有可用选点宿主能力。
  Notify.warn('当前环境不支持选点，请在小程序或新版 APP 内使用');
  console.warn('[选点] env=h5 且无可用选点宿主能力', {
    easyearn: ee,
    hasWxMini: typeof wx !== 'undefined' && !!(wx && wx.miniProgram)
  });
}

function isValidLngLat(lat, lng) {
  const nLat = Number(lat);
  const nLng = Number(lng);
  return Number.isFinite(nLat) && Number.isFinite(nLng) && Math.abs(nLat) <= 90 && Math.abs(nLng) <= 180;
}

function getFallbackLngLat() {
  const selected = mapState.userSelectedLngLat || {};
  if (isValidLngLat(selected.lat, selected.lng)) return { lat: Number(selected.lat), lng: Number(selected.lng) };
  const current = mapState.pointLngLat || {};
  if (isValidLngLat(current.lat, current.lng)) return { lat: Number(current.lat), lng: Number(current.lng) };
  const storeLocation = String(store.state.location || '').split(',');
  if (storeLocation.length === 2 && isValidLngLat(storeLocation[0], storeLocation[1])) {
    return { lat: Number(storeLocation[0]), lng: Number(storeLocation[1]) };
  }
  return { lat: 30.385129, lng: 114.332731 };
}

function readTMapCenter(center) {
  if (!center) return null;
  const lat = typeof center.getLat === 'function' ? center.getLat() : center.lat;
  const lng = typeof center.getLng === 'function' ? center.getLng() : center.lng;
  return isValidLngLat(lat, lng) ? { lat: Number(lat), lng: Number(lng) } : null;
}

function openAppH5LocationPicker() {
  const TMap = window['TMap'];
  if (!TMap) {
    Notify.warn('地图组件加载中，请稍后重试');
    console.warn('[选点][APP-H5] TMap SDK 未加载，无法打开 H5 兜底选点');
    return;
  }
  appLocationPickerVisible.value = true;
  appLocationPickerAddress.value = formData.detailAddress || '';
  nextTick(() => {
    const container = document.getElementById('appH5LocationMap');
    if (!container) return;
    const center = getFallbackLngLat();
    try {
      appLocationPickerMap = new TMap.Map(container, {
        zoom: 17,
        viewMode: '2d',
        pitch: 0,
        center: new TMap.LatLng(center.lat, center.lng),
        showControl: true,
      });
    } catch (err) {
      console.error('[选点][APP-H5] 初始化地图失败:', err);
      appLocationPickerVisible.value = false;
      Notify.warn('地图打开失败，请稍后重试');
    }
  });
}

function closeAppH5LocationPicker() {
  appLocationPickerVisible.value = false;
  if (appLocationPickerMap && typeof appLocationPickerMap.destroy === 'function') {
    try { appLocationPickerMap.destroy(); } catch (e) { /* ignore */ }
  }
  appLocationPickerMap = null;
}

function confirmAppH5LocationPicker() {
  if (!appLocationPickerMap || typeof appLocationPickerMap.getCenter !== 'function') {
    Notify.warn('地图未就绪，请稍后重试');
    return;
  }
  const center = readTMapCenter(appLocationPickerMap.getCenter());
  if (!center) {
    Notify.warn('选点失败，请重新移动地图');
    return;
  }
  mapState.userSelectedLngLat = { lat: center.lat, lng: center.lng };
  mapState.userSelectPoint = true;
  // 同步刷新已有媒体坐标
  locationPics.forEach(pic => { pic.lat = center.lat; pic.lng = center.lng; });
  reverseGeocode(center.lat, center.lng);
  closeAppH5LocationPicker();
  nextTick(() => { initMap(); });
}

// 跳选点前持久化表单 + 媒体附件，回跳后由 restoreFromStorage 恢复
// （之前只存了 formData，没存 locationPics，导致选点回来"已上传的图片/视频丢失"）
//
// ★ 关键修复：快照必须带上"本次上报唯一标识 + 正在选点标志"，避免串单污染 ★
// 历史 bug：localStorage 里的 'voice_report_form_data' 是全局共用 key，
// 上一次某条上报跳选点后如果用户中途退出（没走到 restoreFromStorage 末尾的 removeItem），
// 快照会长期残留；下次进入另一条上报点击选点回跳时，restoreFromStorage 会把前一条的
// locationPics 灌到当前页，表现为"编辑地图位置后，附件里自动出现非当前上报的图片/视频"。
//
// 修复方式：
//   1) 写快照时记录 reportKey（orderId 优先，否则用 rawData 兜底生成的稳定串）
//   2) 同时写 picking 标志 'voice_report_picking_location'='1'
//   3) restoreFromStorage 只在 picking=1 且 reportKey 匹配当前上报时才恢复
//   4) 不匹配或无 picking 标志时，直接清掉残留快照
function getCurrentReportKey() {
  // 优先用 orderId（编辑已有上报场景）；新建上报走 rawData.orderId / originalText 兜底，
  // 避免不同"新建上报"之间也串单。
  const orderId = getCurrentOrderId();
  if (orderId) return `order:${orderId}`;
  // 新建单没有 orderId，用 rawData 的稳定字段组合兜底；实在没有就 new
  const raw = rawData.value || {};
  const fp = [raw.originalText, raw.time, raw.locationDetail].filter(Boolean).join('|');
  return fp ? `new:${fp}` : 'new:empty';
}

function normalizeQueryValue(value) {
  if (Array.isArray(value)) return normalizeQueryValue(value[0]);
  if (value === undefined || value === null) return '';
  const v = String(value).trim();
  return v && v !== 'undefined' && v !== 'null' ? v : '';
}

function getOrderIdFromData(data = {}) {
  const d = data || {};
  return normalizeQueryValue(d.orderId || d.order_id || d.id || d.reportId || d.report_id);
}

function getCurrentOrderId() {
  const raw = rawData.value || {};
  return normalizeQueryValue(formData && formData.orderId)
    || getOrderIdFromData(raw)
    || normalizeQueryValue(route.query.orderId || route.query.order_id || route.query.reportId || route.query.report_id);
}

function ensureVoiceReportSessionId() {
  if (voiceReportSessionId.value) return voiceReportSessionId.value;
  const routeSid = normalizeQueryValue(route.query.vr_sid);
  if (routeSid) {
    voiceReportSessionId.value = routeSid;
    return routeSid;
  }
  const sid = `vr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  voiceReportSessionId.value = sid;
  return sid;
}

function saveFormStateForPickLocation() {
  try {
    const snapshot = {
      reportKey: getCurrentReportKey(),
      sessionId: ensureVoiceReportSessionId(),
      savedAt: Date.now(),
      formData: { ...formData },
      locationPics: JSON.parse(JSON.stringify(locationPics)),
      mapState: {
        userSelectedLngLat: { ...mapState.userSelectedLngLat },
        pointLngLat: { ...mapState.pointLngLat },
        userSelectPoint: mapState.userSelectPoint,
        locationWayName: mapState.locationWayName,
      },
      hasRawAiAnswer: hasRawAiAnswer.value,
      rawData: rawData.value,
      userOpenId: ensureUserOpenId(),
    };
    localStorage.setItem('voice_report_form_data', JSON.stringify(snapshot));
    localStorage.setItem('voice_report_edit_mode', isEditMode.value ? '1' : '0');
    // picking 标志：仅在选点回跳的那次渲染里允许恢复快照
    localStorage.setItem('voice_report_picking_location', '1');
  } catch (e) {
    console.warn('[选点] 持久化表单失败:', e);
  }
}

function buildPickLocationReturnPath() {
  const query = {
    edit: isEditMode.value ? '1' : '0',
    mode: isEditMode.value ? 'edit' : 'view',
    vr_sid: ensureVoiceReportSessionId(),
  };
  const openId = ensureUserOpenId();
  if (openId) query.openId = openId;
  const orderId = getCurrentOrderId();
  if (orderId) query.orderId = orderId;
  const qs = Object.keys(query)
    .map(key => `${key}=${encodeURIComponent(query[key])}`)
    .join('&');
  return `${route.path}${qs ? `?${qs}` : ''}`;
}

function getSafeSelectedLngLat() {
  const lat = mapState.userSelectedLngLat.lat;
  const lng = mapState.userSelectedLngLat.lng;
  if (!isValidLngLat(lat, lng)) return null;
  return { lat: String(lat), lng: String(lng) };
}

function openReadOnlyLocationMap() {
  const point = getSafeSelectedLngLat();
  if (!point) {
    Notify.warn('暂无有效位置信息');
    return;
  }
  const env = detectPickLocationEnv();
  if (env === 'miniprogram') {
    if (!mapState.datayime) return;
    goWeixinViewLocation(point);
    mapState.datayime = false;
    setTimeout(() => { mapState.datayime = true }, 3000);
    return;
  }
  if (!mapState.userSelectPoint) {
    mapState.userSelectPoint = true;
    nextTick(() => { initMap(); });
  }
  Notify.warn('当前环境暂不支持打开地图详情');
}

function goWeixinViewLocation(point) {
  const fullPath = buildPickLocationReturnPath();
  const encodedFullPath = encodeURIComponent(fullPath);
  console.log('[选点][小程序][查看] 跳只读地图页', point);
  wx.miniProgram.navigateTo({
    url: `/pages/H5PicLocation/H5PicLocation?userCheckDetail=true&latitude=${encodeURIComponent(point.lat)}&longitude=${encodeURIComponent(point.lng)}&from=h5HuiTu&url=${encodedFullPath}&maxDistance=5000`
  });
}

function goWeixinPicLocation() {
  const fullPath = buildPickLocationReturnPath();
  const encodedFullPath = encodeURIComponent(fullPath);
  // 把表单 + 已上传图片视频 + 地图状态打包持久化（详见 saveFormStateForPickLocation 注释）
  saveFormStateForPickLocation();

  // ★ 关键修复：必须显式传 userCheckDetail=false ★
  // 小程序 H5PicLocation.js getUserLocation() 里的判断：
  //   if (options.userCheckDetail == 'true'
  //       || (options.userCheckDetail == 'false' && options.latitude != '' && options.latitude != 'undefined')) {
  //     latitude = Number(options.latitude)   // 用我们传过去的坐标
  //   }
  //   // 否则 latitude 保持 res.latitude（用户当前位置）
  //
  // 之前 URL 没拼 userCheckDetail，options.userCheckDetail 就是 undefined（不是 'false'），
  // 上面这个 if 永远进不去，导致小程序选点页一律以"用户当前位置"为中心 —— 这就是
  // 用户反馈"点击地图还是定位到用户所在位置"的根因。policeReportPage 之所以正常是因为
  // 它在 URL 里显式拼了 userCheckDetail=false。
  const userCheckDetail = isEditMode.value ? 'false' : 'true';
  const lat = mapState.userSelectedLngLat.lat || '';
  const lng = mapState.userSelectedLngLat.lng || '';
  // lat/lng 必须是非空且非字符串 'undefined'，否则即使带了 userCheckDetail=false
  // 还是会落到默认分支
  const safeLat = (lat === '' || lat === undefined || lat === null) ? '' : String(lat);
  const safeLng = (lng === '' || lng === undefined || lng === null) ? '' : String(lng);

  // ★ 距离限制：voiceReport 链路扩大到 5km ★
  // H5PicLocation.confirmBtn 默认 distance <= 120m 才允许提交，这是为道路上报场景设计的。
  // 但语音上报常出现"事件就在前方/对面/几个路口外"等场景（用户站在原地描述事件），
  // 真实距离可能 1~5km，120m 限制会卡死流程。
  // 通过 URL 透传 maxDistance（单位：米），由 H5PicLocation 端按需放宽。
  const maxDistance = 5000;
  console.log('[选点][小程序] 跳选点页', { userCheckDetail, lat: safeLat, lng: safeLng, maxDistance });
  wx.miniProgram.navigateTo({
    url: `/pages/H5PicLocation/H5PicLocation?userCheckDetail=${userCheckDetail}&latitude=${safeLat}&longitude=${safeLng}&from=h5HuiTu&url=${encodedFullPath}&maxDistance=${maxDistance}`
  });
}

// 初始化腾讯地图
function initMap() {
  const TMap = window['TMap'];
  if (!TMap) {
    console.warn('TMap SDK 未加载');
    return;
  }
  const container = document.getElementById('voiceMapContainer');
  if (!container) {
    console.warn('voiceMapContainer DOM 未就绪');
    return;
  }
  const map = new TMap.Map("voiceMapContainer", {
    zoom: 16,
    viewMode: '2d',
    pitch: 0,
    doubleClickZoom: false,
    draggable: false,
    scrollable: false,
    touchZoomable: false,
    showControl: false,
    renderOptions: { preserveDrawingBuffer: true },
    center: new TMap.LatLng(mapState.userSelectedLngLat.lat, mapState.userSelectedLngLat.lng),
  });
  new TMap.MultiMarker({
    id: 'marker-layer',
    map: map,
    styles: {
      "marker": new TMap.MarkerStyle({
        "width": 20,
        "height": 30,
        "src": 'https://cdn.map.qq.com/miniprogram/images/poiLocation_1104_mark.png'
      })
    },
    geometries: [{
      "id": 'demo',
      "styleId": 'marker',
      "position": new TMap.LatLng(mapState.userSelectedLngLat.lat, mapState.userSelectedLngLat.lng),
    }]
  });
  map.on('click', () => {
    if (!mapState.userSelectPoint) return;
    if (isEditMode.value) {
      onPicLocation();
      return;
    }
    openReadOnlyLocationMap();
  });
}

// 逆地址解析：通过经纬度获取地址描述
function reverseGeocode(lat, lng) {
  const key = 'W5ZBZ-Q5IWU-EICVA-2TYZL-HTNKS-KUFXS';
  const script = document.createElement('script');
  const callbackName = `_reverseGeo_${Date.now()}`;
  window[callbackName] = (res) => {
    if (res && res.result) {
      const addr = res.result.formatted_addresses?.recommend || res.result.address || '';
      if (addr) {
        formData.detailAddress = addr;
      }
    }
    delete window[callbackName];
    script.remove();
  };
  script.src = `https://apis.map.qq.com/ws/geocoder/v1/?location=${lat},${lng}&key=${key}&get_poi=0&output=jsonp&callback=${callbackName}`;
  document.head.appendChild(script);
}

// 正向地理编码：通过地名获取经纬度，成功后自动渲染地图
function forwardGeocode(address, onSuccess) {
  if (!address) return;
  const key = 'W5ZBZ-Q5IWU-EICVA-2TYZL-HTNKS-KUFXS';
  const script = document.createElement('script');
  const callbackName = `_forwardGeo_${Date.now()}`;
  window[callbackName] = (res) => {
    if (res && res.status === 0 && res.result && res.result.location) {
      const { lat, lng } = res.result.location;
      if (lat && lng) {
        mapState.userSelectedLngLat = { lat, lng };
        mapState.userSelectPoint = true;
        nextTick(() => { initMap(); });
        if (typeof onSuccess === 'function') onSuccess();
        console.log('[地图] 正向地理编码成功，渲染地图', { address, lat, lng });
      }
    } else {
      console.warn('[地图] 正向地理编码失败或无结果', { address, res });
    }
    delete window[callbackName];
    script.remove();
  };
  script.onerror = () => {
    console.warn('[地图] 正向地理编码请求失败', { address });
    delete window[callbackName];
    script.remove();
  };
  script.src = `https://apis.map.qq.com/ws/geocoder/v1/?address=${encodeURIComponent(address)}&key=${key}&output=jsonp&callback=${callbackName}`;
  document.head.appendChild(script);
}

// 从小程序选点页返回后恢复数据
// 关键：之前只恢复了 formData，没恢复 locationPics（已上传图片视频）和 mapState，
// 导致用户回来后看到表单里图片/事件类型等被重置（实际上是 onBeforeMount → initFormData
// 根据 reportData 重新初始化了组件状态，覆盖掉了用户的修改）。
// 现在保存了完整快照（详见 saveFormStateForPickLocation），这里按结构恢复。
function restoreFromStorage() {
  // ★ 关键修复：只有当"上一步确实是跳到选点页"时，才允许用快照覆盖当前状态 ★
  // 并且快照的 reportKey 必须与当前上报一致；否则会把上一次其它上报的
  // formData / locationPics 灌进来（用户反馈的"附件里自动出现非当前上报的图片/视频"根因）。
  const isPickingBack = localStorage.getItem('voice_report_picking_location') === '1';
  let snapshot = readVoiceReportPickSnapshot();

  // 不是选点回跳场景 → 残留快照一律丢弃，防止串单
  if (!isPickingBack) {
    if (snapshot) {
      console.log('[选点回跳] 非选点回跳流程，丢弃残留快照');
      clearVoiceReportPickSnapshot();
    }
    snapshot = null;
    if (store.state.fullpath === 'no') return;
  }

  // 有 picking 标志但没有快照，通常是上次选点中断/异常退出后的残留。
  // 这里必须消费掉脏标志，避免下一次正常进入 onBeforeMount 误跳过 initFormData。
  if (isPickingBack && !snapshot) {
    console.warn('[选点回跳] picking 标志存在但快照为空，清理残留状态');
    clearVoiceReportPickSnapshot();
    return;
  }

  // 身份校验：优先使用本次选点 sessionId；老快照无 sessionId 时才退回 reportKey 校验。
  // 注意：选点回跳 URL 为避免 414 已不再携带 reportData，onBeforeMount 可能无法初始化 rawData，
  // 此时 currentKey 会是 new:empty，不能用它误判并丢弃本次选点前保存的附件快照。
  if (snapshot) {
    const routeSessionId = normalizeQueryValue(route.query.vr_sid);
    const snapshotSessionId = normalizeQueryValue(snapshot.sessionId);
    const currentKey = getCurrentReportKey();
    const snapshotKey = snapshot.reportKey || '';
    let shouldDrop = false;
    if (routeSessionId && snapshotSessionId && routeSessionId !== snapshotSessionId) {
      shouldDrop = true;
      console.warn('[选点回跳] sessionId 不匹配，丢弃', { routeSessionId, snapshotSessionId });
    } else if (!routeSessionId && !snapshotSessionId && snapshotKey && currentKey && currentKey !== 'new:empty' && snapshotKey !== currentKey) {
      shouldDrop = true;
      console.warn('[选点回跳] 快照身份不匹配，丢弃', { snapshotKey, currentKey });
    }
    if (shouldDrop) {
      localStorage.removeItem('voice_report_form_data');
      snapshot = null;
    }
  }

  if (snapshot) {
    // 1) 恢复 formData
    //    兼容旧版本（直接存了扁平 formData，没有 formData/locationPics 嵌套）
    const savedFormData = snapshot.formData || snapshot;
    if (savedFormData && typeof savedFormData === 'object') {
      Object.keys(savedFormData).forEach(key => {
        if (key in formData) formData[key] = savedFormData[key];
      });
      // 选点回跳会跳过 initFormData，eventTypes 会回到默认 6 项；
      // 若快照中是旧版/自定义类型，需要重新追加并修正 selectedTypeIndex。
      const restoredTypeIndex = ensureEventType(formData.eventTypeStr);
      if (restoredTypeIndex >= 0) {
        formData.selectedTypeIndex = restoredTypeIndex;
        formData.eventTypeStr = eventTypes[restoredTypeIndex].label;
      }
    }
    // 2) 恢复已上传图片/视频
    if (Array.isArray(snapshot.locationPics)) {
      locationPics.splice(0, locationPics.length, ...normalizeLocationPics(snapshot.locationPics));
    }
    // 3) 恢复地图状态（新坐标会被下面的 reportSearchWX 覆盖，无坐标场景保留快照值）
    if (snapshot.mapState) {
      if (snapshot.mapState.userSelectedLngLat) {
        mapState.userSelectedLngLat = { ...snapshot.mapState.userSelectedLngLat };
      }
      if (snapshot.mapState.pointLngLat) {
        mapState.pointLngLat = { ...snapshot.mapState.pointLngLat };
      }
      mapState.userSelectPoint = !!snapshot.mapState.userSelectPoint;
      mapState.locationWayName = snapshot.mapState.locationWayName || '';
    }
    // 4) 恢复 AI 原始数据标记
    if (typeof snapshot.hasRawAiAnswer === 'boolean') {
      hasRawAiAnswer.value = snapshot.hasRawAiAnswer;
    }
    if (snapshot.rawData) {
      rawData.value = snapshot.rawData;
    }
    const restoredOrderId = getCurrentOrderId();
    if (restoredOrderId) {
      formData.orderId = restoredOrderId;
    }
    const snapshotOpenId = normalizeOpenId(snapshot.userOpenId);
    if (snapshotOpenId && !normalizeOpenId(store.state.userOpenId)) {
      cacheUserOpenId(snapshotOpenId);
    }
  }

  // 选点页通过 reportSearchWX 回传新坐标 + 街道
  if (store.state.reportSearchWX) {
    let wxdat = null;
    try {
      wxdat = JSON.parse(store.state.reportSearchWX);
    } catch (e) {
      console.warn('[选点回跳] 解析 reportSearchWX 失败:', e);
    }
    if (wxdat) {
      const newLng = wxdat.longitude || wxdat.lng || (wxdat.location && wxdat.location.lng) || '';
      const newLat = wxdat.latitude || wxdat.lat || (wxdat.location && wxdat.location.lat) || '';
      mapState.userSelectedLngLat = { lng: newLng, lat: newLat };
      mapState.userSelectPoint = true;
      mapState.locationWayName = wxdat.content || '';
      formData.detailAddress = wxdat.content || wxdat.street || formData.detailAddress;

      // ★ 关键：选点后必须同步刷新所有已上传媒体的坐标 ★
      // 背景：图片/视频上传时会把"当时的 mapState.userSelectedLngLat"存入 pic.lat/pic.lng。
      // 如果用户先上传了媒体，再去选点修正位置，pic 里的旧坐标就和新选点坐标不一致。
      // 提交时 uploadFormDataToCos 里 `parseFloat(pic.lat || mapState.lat)` 优先取 pic.lat，
      // 导致 COS JSON 里 url[].lat 是旧坐标 → 查看详情时渲染到错误位置。
      // 修复：选点成功后用新坐标覆盖所有已有媒体的坐标。
      if (newLat && newLng) {
        locationPics.forEach(pic => {
          pic.lat = newLat;
          pic.lng = newLng;
        });
      }

      // 等待 v-show DOM 更新后再初始化地图，否则容器尺寸为 0 地图渲染空白
      nextTick(() => { initMap(); });
    }
  }

  // picking 标志一次性消费：无论是否恢复成功，都要清掉，避免下次重进误判
  clearVoiceReportPickSnapshot();
  store.commit('SET_fullpath', 'no');
}

// 时间选择器
function showTimePicker() {
  timePickerVisible.value = true;
}

function onTimeConfirm(value) {
  timePickerVisible.value = false;
  if (value) {
    const d = new Date(value);
    const pad = n => String(n).padStart(2, '0');
    formData.reportTime = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}

// 车道选择
function showLanePicker() {
  tempSelectedLanes.value = [...formData.selectedLanes];
  lanePickerVisible.value = true;
}

function toggleLane(value) {
  const idx = tempSelectedLanes.value.indexOf(value);
  if (idx >= 0) {
    tempSelectedLanes.value.splice(idx, 1);
  } else {
    tempSelectedLanes.value.push(value);
  }
}

function confirmLanes() {
  formData.selectedLanes = [...tempSelectedLanes.value];
  lanePickerVisible.value = false;
}

// ==================== 图片上传（复用 policeReportPage 的 COS 逻辑）====================
const Cos = require("cos-js-sdk-v5");
const locationPics = reactive([]);
const uploading = ref(false);
const previewObj = reactive({
  imageList: [],
  initPage: 0,
  showPreviewImg: false,
});

function getMediaIdentity(src = '') {
  const value = String(src || '').trim();
  if (!value) return '';
  return value.replace(/^http:\/\//i, 'https://').replace(/^https:\/\//i, '');
}

function findLocationPicIndex(src) {
  const identity = getMediaIdentity(src);
  if (!identity) return -1;
  return locationPics.findIndex(item => getMediaIdentity(item.srcCopy || item.src) === identity);
}

function upsertLocationPic(newItem) {
  const existingIndex = findLocationPicIndex(newItem.srcCopy || newItem.src);
  if (existingIndex > -1) {
    const oldItem = locationPics[existingIndex];
    locationPics[existingIndex] = {
      ...oldItem,
      ...newItem,
      id: oldItem.id !== undefined ? oldItem.id : existingIndex,
      src: oldItem.src || newItem.src || '',
    };
    return existingIndex;
  }
  locationPics.push(newItem);
  return locationPics.length - 1;
}

function normalizeLocationPics(list) {
  const result = [];
  const seen = new Set();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const identity = getMediaIdentity(item && (item.srcCopy || item.src));
    if (identity && seen.has(identity)) return;
    if (identity) seen.add(identity);
    result.push(item);
  });
  return result;
}

const cosByScene = new Cos({
  getAuthorization: function(options, callback) {
    void options;
    // 发起凭证请求前再兜底一次 openid，避免首进页面 store 未初始化完
    const openid = ensureUserOpenId();
    if (!openid) {
      console.error('[COS排查][credentials] 未拿到 openid，拒绝请求凭证');
      Notify.warn('未获取到用户身份，无法上传，请重新进入页面');
      uploading.value = false;
      callback({ error: 'missing openid' });
      return;
    }
    request("post", getCosCredentialsByScene, {
      scene: 'info_report',
      ext_info: { openid }
    }).then(data => {
      console.log('[COS排查][credentials] 响应:', data);
      if (!data || !data.data || !data.data.credentials) {
        console.error('[COS排查][credentials] 凭证返回异常:', data);
        // 必须回调 error，否则 SDK 会 hang 住直到超时，前端表现为"上传失败"
        Notify.warn('获取上传凭证失败，请稍后重试');
        uploading.value = false;
        callback({ error: (data && data.msg) || '获取COS凭证失败' });
        return;
      }
      const c = data.data.credentials;
      callback({
        TmpSecretId: c.tmp_secret_id,
        TmpSecretKey: c.tmp_secret_key,
        SecurityToken: c.session_token,
        ExpiredTime: c.expired_time
      });
    }).catch(err => {
      console.error('[COS排查][credentials] 请求失败:', err);
      Notify.warn('网络异常，无法获取上传凭证');
      uploading.value = false;
      callback({ error: '获取COS凭证请求失败' });
    });
  }
});

const imageFileInput = ref(null);
const videoFileInput = ref(null);

function getNativeMediaUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (/^http:\/\//i.test(value)) return value.replace(/^http:\/\//i, 'https://');
  if (!/^https?:\/\//i.test(value)) return `https://${value}`;
  return value;
}

function addNativeMediaItem(item, mediaType) {
  const rawUrl = item && (item.url || item.src || item.path);
  const srcCopy = getNativeMediaUrl(rawUrl);
  if (!srcCopy) return;
  const picIndex = locationPics.length;
  locationPics.push({
    id: picIndex,
    src: '',
    srcCopy,
    lat: item.lat || item.latitude || mapState.userSelectedLngLat.lat || mapState.pointLngLat.lat || '',
    lng: item.lng || item.longitude || mapState.userSelectedLngLat.lng || mapState.pointLngLat.lng || '',
    angle: item.angle || item.direction || '',
    time: item.time || item.timestamp || Date.now(),
    tracks: item.tracks || [],
    mediaType,
  });
  uploading.value = true;
  getPresignedUrl([srcCopy], picIndex);
}

function normalizeNativePayload(payload) {
  if (!payload) return [];
  if (typeof payload === 'string') {
    try { return normalizeNativePayload(JSON.parse(payload)); } catch (e) { return [{ url: payload }]; }
  }
  if (Array.isArray(payload)) {
    return payload.map(item => (typeof item === 'string' ? { url: item } : item)).filter(Boolean);
  }
  return [payload];
}

function handleNativeImageCallback(res) {
  console.log('[APP原生拍照回调] onRecivePoiPicPath:', res);
  const remain = Math.max(0, 6 - locationPics.length);
  if (remain <= 0) {
    Notify.warn('最多上传6个文件');
    return;
  }
  normalizeNativePayload(res).slice(0, remain).forEach(item => addNativeMediaItem(item, 'image'));
}

function handleNativeVideoCallback(payload) {
  console.log('[APP原生视频回调] shootVideoCallback:', payload);
  if (locationPics.length >= 6) {
    Notify.warn('最多上传6个文件');
    return;
  }
  const list = normalizeNativePayload(payload);
  const data = list[0] || {};
  const locations = Array.isArray(data && data.locations) ? data.locations : [];
  const tracks = locations.map(item => ({
    lat: String(item.lat || item.latitude || ''),
    lng: String(item.lng || item.longitude || ''),
    timestamp: String(item.time || item.timestamp || ''),
    angle: String(item.direction || item.angle || ''),
  }));
  const firstTrack = tracks[0] || {};
  addNativeMediaItem({
    url: data && (data.url || data.src || data.path),
    lat: firstTrack.lat,
    lng: firstTrack.lng,
    angle: firstTrack.angle,
    time: firstTrack.timestamp,
    tracks,
  }, 'video');
}

// ========== APP 专用上传入口（完全模仿 messageAppearDetailH5.vue 的 clack 实现）==========
// 该路径仅在 APP 模式（v-if="isInApp"）显示对应按钮时使用：
//   - 调 easyearn.takePicture(count) / easyearn.takeBatchPoiVideo()；
//   - 拍/选完后 APP 通过 window.onRecivePicPath / window.shootVideoCallback 回填 COS URL；
//   - 与 input[type=file] 路径互斥：APP 模式不渲染 input，避免 WebView 拦截冲突。
//
// 重要：Android JS Bridge 注入的方法在 WebView 里【typeof 可能不返回 'function'】
// （可能是 "object" 或其他），但依然可以被正常调用。因此本文件不做 typeof 检测，
// 和 messageAppearDetailH5.vue 的 clack() 保持一致，直接调用，try/catch 兜底。
function logEasyearnEnv(tag) {
  const ee = getEasyearn();
  let ownProps = null;
  let forInKeys = null;
  if (ee) {
    try { ownProps = Object.getOwnPropertyNames(ee); } catch (e) { ownProps = `err:${e.message}`; }
    try { forInKeys = []; for (const k in ee) forInKeys.push(k); } catch (e) { forInKeys = `err:${e.message}`; }
  }
  console.log(`[附件][APP] ${tag}`, {
    isH5: store.state.isH5,
    hasEasyearn: !!ee,
    easyearnKeys: ee ? Object.keys(ee) : null,
    ownProps,
    forInKeys,
    takePicture_typeof: ee && typeof ee.takePicture,
    takePoiPicture_typeof: ee && typeof ee.takePoiPicture,
    takeBatchPoiVideo_typeof: ee && typeof ee.takeBatchPoiVideo,
    takePicture_truthy: !!(ee && ee.takePicture),
    takePoiPicture_truthy: !!(ee && ee.takePoiPicture),
    takeBatchPoiVideo_truthy: !!(ee && ee.takeBatchPoiVideo),
    getVersionCode_typeof: ee && typeof ee.getVersionCode,
    versionCode: (ee && ee.getVersionCode) ? safeCall(() => ee.getVersionCode()) : null,
  });
}

function safeCall(fn) {
  try { return fn(); } catch (e) { return `err:${e && e.message}`; }
}

function onAppPickImage() {
  logEasyearnEnv('onAppPickImage');
  if (locationPics.length >= 6) {
    Notify.warn('最多上传6个文件');
    return;
  }
  const ee = getEasyearn();
  if (!ee) {
    Notify.warn('当前环境不支持拍照');
    return;
  }
  const remain = 6 - locationPics.length;
  // 完全照抄 messageAppearDetailH5.vue clack() 的 APP 分支——不做 typeof 检测。
  let versionCode = 0;
  try { versionCode = Number(ee.getVersionCode && ee.getVersionCode()) || 0; } catch (e) { /* ignore */ }
  console.log('[附件][APP][图片] versionCode=', versionCode, 'remain=', remain);

  // 按优先级依次尝试调用（不做 typeof 检测，真正调用时捕获异常）：
  //   1) 高版本（>=32700000）首选 takePicture(false, count)
  //   2) 次选 takePicture(count)
  //   3) 再次选 takePoiPicture(count)
  const attempts = [];
  if (versionCode >= 32700000) {
    attempts.push({ name: 'takePicture(false,remain)', call: () => ee.takePicture(false, remain) });
  }
  attempts.push({ name: 'takePicture(remain)', call: () => ee.takePicture(remain) });
  attempts.push({ name: 'takePoiPicture(remain)', call: () => ee.takePoiPicture(remain) });

  for (const a of attempts) {
    try {
      console.log('[附件][APP][图片] 尝试调用', a.name);
      a.call();
      console.log('[附件][APP][图片] 调用成功', a.name);
      return;
    } catch (err) {
      console.warn('[附件][APP][图片] 调用失败', a.name, err && err.message);
    }
  }
  Notify.warn('当前 APP 版本不支持拍照，请升级');
  console.error('[附件][APP][图片] 所有桥调用尝试均失败');
}

function onAppPickVideo() {
  logEasyearnEnv('onAppPickVideo');
  if (locationPics.length >= 6) {
    Notify.warn('最多上传6个文件');
    return;
  }
  const ee = getEasyearn();
  if (!ee) {
    Notify.warn('当前环境不支持录像');
    return;
  }
  // 不做 typeof 检测，直接调用。
  try {
    console.log('[附件][APP][视频] 尝试调用 takeBatchPoiVideo');
    ee.takeBatchPoiVideo();
    console.log('[附件][APP][视频] 调用成功');
    return;
  } catch (err) {
    console.warn('[附件][APP][视频] takeBatchPoiVideo 调用失败', err && err.message);
  }
  Notify.warn('当前 APP 版本不支持录像，请升级');
}

// 图片/视频上传通路：
// - APP 端对齐 placeadd/locationDetail.vue，走 easyearn.takePoiPicture / takeBatchPoiVideo，
//   通过 window.onRecivePoiPicPath / window.shootVideoCallback 回填已上传 URL；
// - 小程序 web-view / 普通 H5 继续走 input[type=file] + cos-js-sdk 上传。

// 图片上传
// 与 onVideoFileChange 一致的健壮性处理：
//   - 用 e.target.files 优先，ref 兜底（避免极端场景 ref 失效）；
//   - 6 张限制在 change 里二次校验；
//   - 详细 console 日志便于排查。
function onImageFileChange(e) {
  const fileEl = (e && e.target) || imageFileInput.value;
  const files = fileEl && fileEl.files;
  if (!fileEl || !files || !files[0]) return;
  if (locationPics.length >= 6) {
    Notify.warn('最多上传6个文件');
    if (fileEl) fileEl.value = '';
    return;
  }
  uploading.value = true;
  const file = files[0];
  console.log('[COS排查][onImageFileChange] file:', {
    name: file.name, size: file.size, type: file.type
  });
  let type = 'jpg';
  if (file.type && file.type.indexOf('png') > -1) type = 'png';

  setMiniImg(file).then(miniImgFile => {
    if (miniImgFile) {
      if (miniImgFile.type && miniImgFile.type.indexOf('png') > -1) {
        type = 'png';
      }
      cosPutObject(miniImgFile, type, 'image');
    } else {
      // 压缩未产出结果，直接上传原图
      cosPutObject(file, type, 'image');
    }
  }).catch((err) => {
    // 压缩失败（如 HEIC 等不支持格式）不阻断上传，直接上传原图
    console.warn('[COS排查][setMiniImg] 压缩失败，改为直接上传原图:', err);
    cosPutObject(file, type, 'image');
  });
  fileEl.value = '';
}

// 视频上传
// 关键兼容点（APP WebView / iOS Safari / 微信 web-view 三端踩过的坑）：
// 1) 用户录像后，部分 WebView 走 change 事件时 ref.value.files 已被刷新到新引用，
//    某些版本里旧的 ref 指针拿到的是空数组；改成同时支持 e.target.files 兜底。
// 2) APP 录像返回的 File.name 经常没有扩展名（如 "video_capture" / 空串），
//    用 split('.').pop() 取后缀拿到的会是文件名本身，COS Key 后缀异常导致服务端
//    Content-Type 推断失败（虽不一定 400，但后续读取链路会出问题）。
//    这里参考 photo.vue 的实现，video 后缀强制规整为 mp4/mov/webm 等白名单内的扩展名。
// 3) 用户录完视频如果 files[0] 仍取不到（部分 Android WebView 把视频写入失败时
//    会以空 files 触发 change），不能再静默 return，否则用户感知就是"点了没反应"，
//    这里给出明确提示，并写入排查日志。
function onVideoFileChange(e) {
  const fileEl = (e && e.target) || videoFileInput.value;
  const files = fileEl && fileEl.files;
  console.log('[COS排查][onVideoFileChange] files:', files && Array.from(files).map(f => ({
    name: f.name, size: f.size, type: f.type, lastModified: f.lastModified,
  })));
  if (!fileEl || !files || !files[0]) {
    console.warn('[COS排查][onVideoFileChange] files 为空，可能是用户取消，或 WebView 未把录像文件写回 input');
    return;
  }
  if (locationPics.length >= 6) {
    Notify.warn('最多上传6个文件');
    if (fileEl) fileEl.value = '';
    return;
  }
  const file = files[0];
  if (!file.size) {
    // 部分 APP/WebView 录像异常时会回传 size=0 的占位文件，直接上传会得到一个空对象
    Notify.warn('视频读取失败，请重新录制或选择');
    console.warn('[COS排查][onVideoFileChange] file.size=0，疑似 WebView 录像写回失败');
    if (fileEl) fileEl.value = '';
    return;
  }
  // 视频大小限制 50MB
  // if (file.size > 50 * 1024 * 1024) {
  //   Notify.warn('视频不能超过50MB');
  //   return;
  // }
  uploading.value = true;
  // 推断扩展名：优先 file.name 后缀，其次 file.type，最终兜底 mp4
  const ext = pickVideoExt(file);
  console.log('[COS排查][onVideoFileChange] 推断后缀 ext=', ext);
  cosPutObject(file, ext, 'video');
  if (fileEl) fileEl.value = '';
}

/**
 * 从 File 对象推断视频扩展名（不依赖 file.name 的"."）：
 * 1) file.name 显式带 . 后缀（mp4/mov/m4v/webm/avi/wmv/flv/mkv）→ 直接用；
 * 2) file.type 形如 "video/mp4"/"video/quicktime" → 走 MIME 映射；
 * 3) 兜底 mp4。
 */
function pickVideoExt(file) {
  const ALLOW = ['mp4', 'mov', 'm4v', 'webm', 'avi', 'wmv', 'flv', 'mkv', '3gp'];
  const name = (file && file.name) || '';
  const dotIdx = name.lastIndexOf('.');
  if (dotIdx > -1 && dotIdx < name.length - 1) {
    const e = name.slice(dotIdx + 1).toLowerCase();
    if (ALLOW.includes(e)) return e;
  }
  const type = ((file && file.type) || '').toLowerCase();
  if (type.includes('quicktime')) return 'mov';
  if (type.includes('mp4')) return 'mp4';
  if (type.includes('webm')) return 'webm';
  if (type.includes('x-matroska')) return 'mkv';
  if (type.includes('3gpp')) return '3gp';
  return 'mp4';
}

function cosPutObject(fileItem, type, mediaType) {
  // 上传前再兜底一次；若仍无 openId，直接提示用户重新进入，避免 Key 里出现 'default'
  // 导致 COS 服务端鉴权失败（403 AccessDenied）。
  const uid = ensureUserOpenId();
  if (!uid) {
    Notify.warn('未获取到用户身份，无法上传，请重新进入页面');
    uploading.value = false;
    return;
  }
  const key = `easyearn/info_report/${uid}/road/evaluating/${uid}/${new Date().getTime()}.${type}`;
  console.log('[COS排查][putObject] 开始上传', { key, type, mediaType, size: fileItem && fileItem.size });
  cosByScene.putObject(
    {
      Bucket: 'mapugc-user-1251316161',
      Region: 'ap-beijing',
      Key: key,
      StorageClass: "STANDARD",
      Body: fileItem,
      onProgress: function() {}
    },
    function(err, datas) {
      console.log('[COS排查][putObject] 回调', { err, datas });
      if (err || !datas) {
        const errMsg = (err && (err.error || err.message || err.code)) || '未知错误';
        console.error('[COS排查][putObject] 上传失败:', errMsg, err);
        Notify.warn("上传失败：" + errMsg);
        uploading.value = false;
        return;
      }
      if (datas.Location) {
        const newItem = {
          id: locationPics.length,
          src: '',
          srcCopy: `https://${datas.Location}`,
          lat: mapState.userSelectedLngLat.lat || mapState.pointLngLat.lat || '',
          lng: mapState.userSelectedLngLat.lng || mapState.pointLngLat.lng || '',
          mediaType: mediaType,
        };
        locationPics.push(newItem);
        getPresignedUrl([datas.Location], locationPics.length - 1);
      } else {
        // datas 存在但没有 Location，异常兜底
        console.warn('[COS排查][putObject] datas存在但无Location:', datas);
        Notify.warn("上传失败：响应异常");
        uploading.value = false;
      }
    }
  );
}

// 预览视频
// 之前的实现分环境跳转：
//   - 小程序：wx.miniProgram.navigateTo('/pages/webview/webview?url=...') —— 小程序里根本没有这个页面，
//     桥调用虽返回 invokeMiniProgramAPI:ok（只代表 H5→小程序通信成功），但实际页面不存在，
//     用户点击后没有任何反应（日志正好印证：begin / end 全是 ok，却没任何后续）。
//   - H5：window.open(src) —— iOS Safari / 微信 web-view 内常被拦截，且 .MOV 等格式无法直接渲染。
//
// 小程序 web-view 的 wx.miniProgram 只暴露 navigate/postMessage 等有限 API，
// 无法调用 wx.previewMedia；而小程序端又没有对应的预览页面。
// 因此统一改为：在当前 H5 页面内用全屏覆盖层 <video> 播放，适用于 H5 / 小程序 web-view / APP 三端。
// APP 端若宿主注入了 easyearn.openVideo 则优先交给原生播放；否则走同一套 H5 覆盖层兜底。
function onPreviewVideo(src) {
  if (!src) {
    Notify.warn('视频地址无效，无法预览');
    return;
  }
  console.log('[视频预览] 开始预览视频:', src);

  // 规范化 URL，避免 http:// 在 iOS ATS 下被拦截
  let url = src;
  if (/^http:\/\//i.test(url)) url = url.replace(/^http:\/\//i, 'https://');
  if (!/^https?:\/\//i.test(url)) {
    Notify.warn('视频地址格式不正确，无法预览');
    return;
  }

  // APP 环境：优先用原生播放器（若宿主提供）
  if (!isInMiniProgram && window.easyearn && typeof window.easyearn.openVideo === 'function') {
    try {
      window.easyearn.openVideo(url);
      return;
    } catch (error) {
      console.warn('[视频预览] App 原生预览失败，降级 H5 覆盖层:', error);
    }
  }

  // 默认统一走 H5 全屏覆盖层（小程序 web-view / 普通 H5 / APP 无原生能力 均适用）
  openVideoOverlay(url);
}

// 当前页面内的全屏覆盖层播放，避免依赖 window.open / 小程序跳转
let _videoOverlayEl = null;
function openVideoOverlay(src) {
  try {
    // 已有遮罩则先清理，避免重复插入
    closeVideoOverlay();

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;top:0;left:0;right:0;bottom:0;width:100%;height:100%;z-index:99999;background:#000;display:flex;align-items:center;justify-content:center;';

    const videoEl = document.createElement('video');
    videoEl.src = src;
    videoEl.controls = true;
    videoEl.autoplay = true;
    videoEl.playsInline = true;
    videoEl.setAttribute('playsinline', 'true');
    videoEl.setAttribute('webkit-playsinline', 'true');
    videoEl.setAttribute('x5-playsinline', 'true');
    // 不加 crossorigin：该属性会让浏览器走 CORS 预检，而 COS 预签名 URL 对 MOV
    // 等流媒体并不稳定返回 Access-Control-Allow-Origin，容易触发 PARSE_ERROR。
    // 预览本身不需要读取像素，去掉后能大幅减少误报。
    videoEl.style.cssText = 'max-width:100%;max-height:100%;width:100%;height:auto;background:#000;';

    // 播放状态机：只有"从未进入可播放态"时的错误才算真失败
    let hasPlayable = false;
    let hasNotified = false;
    const markPlayable = () => { hasPlayable = true; };
    videoEl.addEventListener('loadeddata', markPlayable);
    videoEl.addEventListener('canplay', markPlayable);
    videoEl.addEventListener('playing', markPlayable);
    videoEl.addEventListener('error', function() {
      // 关闭时把元素从 DOM 卸载也可能冒一次 error，此时 overlay 已为空，直接忽略
      if (!_videoOverlayEl) return;
      // 视频已经能播了，后续 error 多为"切源/清空"引起的 PARSE_ERROR 误报
      if (hasPlayable || (videoEl.readyState && videoEl.readyState >= 2)) {
        console.warn('[视频预览] 已可播放后触发 error，忽略:', videoEl.error);
        return;
      }
      if (hasNotified) return;
      hasNotified = true;
      console.error('[视频预览] 播放失败', videoEl.error);
      Notify.warn('视频播放失败，可能是格式不支持（如 MOV）或网络问题');
    });

    const closeBtn = document.createElement('div');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = 'position:absolute;top:calc(env(safe-area-inset-top, 20px) + 10px);right:16px;width:36px;height:36px;background:rgba(0,0,0,0.55);color:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:22px;line-height:1;cursor:pointer;z-index:1;';
    closeBtn.onclick = closeVideoOverlay;

    overlay.appendChild(videoEl);
    overlay.appendChild(closeBtn);
    document.body.appendChild(overlay);
    _videoOverlayEl = overlay;
  } catch (error) {
    console.error('[视频预览] 创建覆盖层失败:', error);
    Notify.warn('视频预览失败，请稍后重试');
  }
}

function closeVideoOverlay() {
  const el = _videoOverlayEl;
  // 先把引用置空，这样即使下面的 DOM 操作触发了一次 error 事件，
  // error handler 里 `if (!_videoOverlayEl) return;` 也能直接丢弃，
  // 不会误弹 "视频播放失败" 的 Notify。
  _videoOverlayEl = null;
  if (el && el.parentNode) {
    try {
      const v = el.querySelector('video');
      if (v) {
        v.pause();
        // 注意：不再执行 v.src = ''，置空 src 会让浏览器立刻抛 MEDIA_ERR_SRC_NOT_SUPPORTED
        // / PARSE_ERROR，从而误触发 onerror。移除 DOM 节点后，video 会被 GC，无需手动清源。
        v.removeAttribute('src');
        v.load(); // 让浏览器停止正在进行的下载/解码
      }
    } catch (e) { /* ignore */ }
    el.parentNode.removeChild(el);
  }
}

function getPresignedUrl(res, pIndex) {
  // 历史数据中有些 src 以 http:// 开头；真机（iOS ATS）和后端签名接口都要求 https，
  // 否则会返回 40404 "请确认是否配置bucket"，导致 locationPics[i].src 一直为空，
  // 模板里 v-if="item.src" 不渲染，表现为"看不到图片"。这里统一规范为 https://。
  const normalizeUrl = (u) => {
    if (!u) return u;
    if (/^http:\/\//i.test(u)) return u.replace(/^http:\/\//i, 'https://');
    if (!/^https?:\/\//i.test(u)) return `https://${u}`;
    return u;
  };
  res.forEach((item, index) => {
    const picIndex = pIndex !== undefined ? pIndex : index;
    const cosurl = normalizeUrl(item);
    console.log('[COS排查][confirm-media] picIndex=', picIndex, ' 原始=', item, ' 规范化=', cosurl);
    window.getReadOnlyPresignedUrlApi(cosurl).then(url => {
      console.log('[COS排查][confirm-media] 签名成功 picIndex=', picIndex, ' src=', url);
      if (locationPics[picIndex]) {
        locationPics[picIndex].src = url;
      }
      uploading.value = false;
    }).catch(err => {
      console.warn('[COS排查][confirm-media] 签名失败 picIndex=', picIndex, ' cosurl=', cosurl, ' err=', err);
      uploading.value = false;
    });
  });
}

function onPreviewImage(index) {
  previewObj.imageList = JSON.parse(JSON.stringify(locationPics));
  previewObj.initPage = index;
  previewObj.showPreviewImg = true;
}

function uploadDelete(index) {
  locationPics.splice(index, 1);
}

// 返回
function goBack() {
  localStorage.removeItem('voice_report_edit_mode');
  // 用户主动放弃本次查看/编辑会话，一并清理详情数据缓存，
  // 避免下次以非历史入口进入确认页时误读残留数据。
  localStorage.removeItem('voice_report_data');
  // ★ 清理选点快照 + picking 标志 ★
  // 防止本次会话残留的快照被下一条上报的 restoreFromStorage 吃掉，
  // 导致"附件里自动出现非当前上报的图片/视频"。
  localStorage.removeItem('voice_report_form_data');
  localStorage.removeItem('voice_report_picking_location');

  // 实时读取 easyearn，避免宿主异步注入时顶层缓存拿不到
  const ee = window.easyearn;

  // 1. SPA 内部有上一页，优先回退到上一个 H5 路由
  //    典型场景：从"上报历史"列表页 router.push 进入当前页，
  //    用户期望的"返回"就是回到历史列表，而不是跳回 APP 原生录入页。
  //    说明：Vue Router 进入首屏时 history.state.back 为 null，
  //    所以这里不会误伤"APP 原生直接打开确认页"的场景。
  if (window.history.state && window.history.state.back) {
    router.go(-1);
    return;
  }

  // 2. APP(easyearn) 环境：SPA 没有上一页，交给原生回到 APP 的上报录入页
  if (ee) {
    try {
      if (typeof ee.goBackToPage === 'function') {
        ee.goBackToPage('reprot');
      } else if (typeof ee.goBack === 'function') {
        ee.goBack();
      } else {
        // easyearn 存在但没有可用的返回方法，降级到 history 回退
        router.go(-1);
      }
    } catch (err) {
      console.error('[voiceReportConfirm] easyearn goBack 失败，降级 router.go(-1)', err);
      router.go(-1);
    }
    return;
  }

  // 3. 小程序 web-view 直接加载的，返回小程序上一页
  if (isInMiniProgram) {
    wx.miniProgram.navigateBack();
    return;
  }

  // 4. 兜底
  router.go(-1);
}

// 提交 / 保存草稿成功后，跳转到上报历史列表（兼容 H5 / 小程序 / APP）
function goToReportHistory() {
  // 1. 小程序 web-view 环境：通过 redirectTo 跳转到加载历史页的 webview 壳
  //    这样小程序的页面栈正确，用户返回不会回到已提交的确认页
  //    注意：这里不能用 store.state.isH5，APP 端通过 type=h5page 打开时该值也会为 true
  if (isInMiniProgram) {
    try {
      wx.miniProgram.redirectTo({
        url: '/pages/h5webNoNav/h5webNoNav?url=/voice_report_history&voiceReport=true'
      });
      return;
    } catch (e) {
      console.error('小程序跳转历史页失败，降级到 H5 路由', e);
    }
  }
  // 2. APP(easyearn) 环境 & 普通 H5 环境：同为 SPA，直接使用 router.replace
  //    replace 而非 push，避免用户从历史页按返回又回到已提交的确认页
  router.replace('/voice_report_history');
}

// 表单校验
function validator() {
  // reportName 不在 UI 中展示，如果为空自动填充默认值
  if (!formData.reportName) {
    formData.reportName = '交通路况上报单';
  }
  if (formData.eventTypeStr === '' && formData.selectedTypeIndex < 0) {
    Notify.warn("请选择事件类型");
    return false;
  }
  if (!formData.detailAddress) {
    Notify.warn("请填写地点信息");
    return false;
  }
  if (!formData.reportTime) {
    Notify.warn("请选择事件时间");
    return false;
  }
  return true;
}

// 提交/保存草稿
async function submitReport(submitState) {
  if (submitting.value) return;
  if (submitState === 40 && !validator()) return;

  const raw = rawData.value || {};
  // 语音上报链路（voiceReport.js → h5webNoNav → 本页）跳转时，URL 上的 openId 有可能
  // 因为小程序侧 storage 尚未就绪而为空，导致 store.state.userOpenId 初始化时拿不到。
  // 这里和 COS 凭证请求一样，统一走 ensureUserOpenId 做多源兜底（URL query / hash /
  // window.easyearn / store.state.userInfo / localStorage / sessionStorage）。
  const openId = ensureUserOpenId();
  if (!openId) {
    Notify.warn('未获取到用户身份，请返回重新进入页面');
    console.warn('[submitReport] 未获取到 openId，拒绝提交');
    return;
  }

  submitting.value = true;

  const locationArr = (store.state.location || '0,0').split(',');

  try {
    // 1. 将表单数据（含图片URL）打包为 JSON 上传到 COS，获取 cosUrl
    const cosUrl = await uploadFormDataToCos();
    console.log('COS JSON 上传完成, cosUrl:', cosUrl);

    console.log(formData, 'formData');

    // 2. 构建请求体
    // 关键：必须把经纬度持久化到后端 / cosUrl JSON 中。
    // 之前未提交 lat/lng/eventLocation，导致：
    //   - 历史/草稿编辑时 initFormData 拿不到坐标 → 又得在前端 geocodeAddress 解析，
    //     既慢又可能解析失败（如 AI 返回的"附近"类描述）；
    //   - 跳小程序选点页时若 geocode 还在异步进行中或失败，URL 里 lat/lng 是空，
    //     选点页落到默认"用户当前位置"分支，导致定位错误。
    // 提交侧字段命名按后端历史习惯：lat / lng / eventLocation（"lat,lng" 字符串）。
    const submitLat = mapState.userSelectedLngLat.lat || '';
    const submitLng = mapState.userSelectedLngLat.lng || '';
    const requestBody = {
      common: {
        openid: openId,
        version: "3.16.0",
        module_id: 1,
        vcode: "31600000",
        loc_x: parseFloat(locationArr[0]) || 0,
        loc_y: parseFloat(locationArr[1]) || 0,
        token: store.state.accessToken || '',
        imei: '',
      },
      reportName: formData.reportName,
      detailAddress: formData.detailAddress,
      reportTime: formData.reportTime,
      eventTypeStr: formData.eventTypeStr || eventTypes[formData.selectedTypeIndex]?.label || '',
      impactLevel: formData.impactLevel,
      lanesAffected: formData.selectedLanes,
      briefDescription: formData.briefDescription,
      originalText: formData.originalText,
      cosUrl: cosUrl,
      reportType: 9,
      submitState: submitState,
      manuallyEdited: checkManuallyEdited(),
      // 经纬度回传：lat/lng 单独字段 + eventLocation "lat,lng" 字符串双兜底，
      // initFormData 已经支持这两种格式回填（见 lat||latitude / eventLocation 分支）。
      lat: submitLat,
      lng: submitLng,
      eventLocation: (submitLat && submitLng) ? `${submitLat},${submitLng}` : '',
      // 编辑模式下后端需要带回原单 id 做更新，历史列表接口字段命名可能是
      // 这里做一次兼容取值，优先 orderId，按常见命名依次兜底，避免因字段名
      // 不一致导致传 0 → 后端误判为新建单。
      orderId: isEditMode.value
        ? getCurrentOrderId() || 0
        : 0,
      // 仅当本次进入确认页是"语音识别后首次提交"（入参带 _rawData）时，
      // 才回传 originalAiAnswer 保存 AI 原始识别结果。
      // 历史入口（草稿/已提交单二次编辑）时 rawData 实为用户上次提交的值，
      // 若再回传会污染后端已留存的 AI 原始快照。
      ...(hasRawAiAnswer.value ? {
        originalAiAnswer: {
          reportName: raw.reportName || '',
          province: raw.province || '',
          city: raw.city || '',
          district: raw.district || '',
          streetAddress: raw.streetAddress || '',
          detailAddress: raw.detailAddress || '',
          reportTime: raw.reportTime || '',
          eventType: raw.eventType || '',
          eventTypeStr: raw.eventTypeStr || '',
          impactLevel: raw.impactLevel || 0,
          // AI 接口返回的 lanesAffected 是单个数字（见 voiceReport.js LANES_DICT 的用法），
          // 但后端字段 schema 期望数组（与主请求体 lanesAffected: formData.selectedLanes 对齐），
          // 这里做一次归一化：数组保持、数字包成数组、空值兜底为空数组。
          lanesAffected: Array.isArray(raw.lanesAffected)
            ? raw.lanesAffected
            : (raw.lanesAffected !== undefined && raw.lanesAffected !== null && raw.lanesAffected !== ''
                ? [raw.lanesAffected]
                : []),
          briefDescription: raw.briefDescription || '',
          originalText: raw.originalText || '',
          confidence: raw.confidence || 0,
        },
      } : {}),
    };

    // 3. 调用提交接口
    console.log('提交接口地址:', aiVoiceInfoReport);
    console.log('提交请求体:', JSON.stringify(requestBody));
    const res = await request("post", aiVoiceInfoReport, requestBody);
    console.log('提交响应:', res);
    submitting.value = false;
    if (res && res.code === 0) {
      localStorage.removeItem('voice_report_edit_mode');
      // 本次上报/编辑会话已结束，清理详情数据缓存，避免下次以非历史入口
      // 进入确认页时被 onBeforeMount 的 localStorage 兜底分支误读为旧内容。
      // 注意：清理时机必须在接口成功之后，而非 onBeforeMount 读取后立即清理——
      // 否则用户在 confirm 页和选点/子页之间来回跳转时，数据会被过早清空。
      localStorage.removeItem('voice_report_data');
      // 同样清理选点快照，防止残留污染下一条上报
      localStorage.removeItem('voice_report_form_data');
      localStorage.removeItem('voice_report_picking_location');
      Notify.success(submitState === 40 ? "提交成功" : "草稿已保存");
      // 提交/保存草稿成功后跳转到上报历史列表（兼容 H5 / 小程序 / APP 三端）
      setTimeout(() => {
        goToReportHistory();
      }, 1000);
    } else {
      Notify.warn(res?.msg || "提交失败，请重试");
    }
  } catch (err) {
    submitting.value = false;
    console.error('提交失败', err);
    Notify.warn("网络异常，请重试");
  }
}

/**
 * 将表单数据打包为 JSON 上传到 COS，返回 cosUrl
 * 与 policeReportPage 的 FillInEvent 逻辑一致
 */
function uploadFormDataToCos() {
  return new Promise((resolve) => {
    const uid = store.state.userOpenId || 'default';
    const key = `easyearn/info_report/${uid}/road/evaluating/${uid}/${new Date().getTime()}/${Date.now()}.json`;

    // 收集已上传的媒体文件列表
    // ★ url[].lat/lng 统一取 mapState 的最新坐标（事件地点），不再用 pic.lat 兜底 ★
    // 原因：pic.lat 记录的是"上传那一刻"的定位，如果用户之后重新选点，pic.lat 是旧值。
    // 虽然选点回跳时我们已经同步刷新了 pic.lat，这里再加一道保险：
    // 以提交时刻的 mapState.userSelectedLngLat 为最终权威值。
    const eventLat = parseFloat(mapState.userSelectedLngLat.lat) || 0;
    const eventLng = parseFloat(mapState.userSelectedLngLat.lng) || 0;
    const urlList = [];
    locationPics.forEach((pic, index) => {
      if (pic.srcCopy) {
        urlList.push({
          index: index,
          src: pic.srcCopy,
          lat: eventLat,
          lng: eventLng,
          angle: pic.angle || 0,
          time: pic.time || Date.now(),
          text: '',
          mediaType: pic.mediaType || 'image',
          tracks: pic.tracks || [],
        });
      }
    });

    // lanesAffected 多选，传 label 数组
    const lanesAffectedLabels = formData.selectedLanes
      .map(v => laneOptions.find(o => o.value === v)?.label)
      .filter(Boolean);

    // 打包全部表单数据为 JSON
    // 同样把 lat/lng/eventLocation 写进来，作为接口字段缺失时的二级兜底。
    // initFormData 走 cosUrl 兜底分支（loadMediaFromCosUrl）时会把这些字段回填。
    const formLat = mapState.userSelectedLngLat.lat || '';
    const formLng = mapState.userSelectedLngLat.lng || '';
    const formJson = {
      reportName: formData.reportName,
      detailAddress: formData.detailAddress,
      reportTime: formData.reportTime,
      eventTypeStr: formData.eventTypeStr || eventTypes[formData.selectedTypeIndex]?.label || '',
      impactLevel: formData.impactLevel,
      lanesAffected: lanesAffectedLabels,
      briefDescription: formData.briefDescription,
      originalText: formData.originalText,
      cosUrl: '',
      reportType: 9,
      orderId: getCurrentOrderId(),
      url: urlList,
      lat: formLat,
      lng: formLng,
      eventLocation: (formLat && formLng) ? `${formLat},${formLng}` : '',
    };

    // 如果没有图片也没关系，JSON 仍然上传
    cosByScene.putObject(
      {
        Bucket: 'mapugc-user-1251316161',
        Region: 'ap-beijing',
        Key: key,
        StorageClass: "STANDARD",
        Body: JSON.stringify(formJson),
        onProgress: function() {}
      },
      function(err, datas) {
        if (err || !datas) {
          console.error('COS JSON 上传失败', err);
          // 上传失败不阻断提交，cosUrl 传空
          resolve('');
          return;
        }
        // 真机（尤其 iOS ATS）强制 https；后端签名接口也要求 https 前缀，否则会返回
        // 40404 "请确认是否配置bucket"。这里保持与 policeReportPage 图片上传一致。
        const cosUrl = `https://${datas.Location}`;
        console.log('COS JSON cosUrl:', cosUrl);
        resolve(cosUrl);
      }
    );
  });
}
</script>

<style lang="scss" scoped>
.voice-report-confirm {
  background-color: #F6F9FF;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

// 导航栏
.navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: calc(env(safe-area-inset-top, 20px) + 12px);
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

  .navbar-back {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .back-arrow {
      width: 10px;
      height: 10px;
      border-left: 2px solid #17181A;
      border-bottom: 2px solid #17181A;
      transform: rotate(45deg);
    }
  }

  .navbar-title {
    font-size: 17px;
    font-weight: 600;
    color: #17181A;
  }

  .navbar-placeholder {
    width: 36px;
  }
}

// 小程序环境：原生顶部导航栏已提供返回和状态栏安全区，H5 自绘 navbar 去掉返回按钮与顶部安全区偏移，标题居中
.navbar-miniprogram {
  justify-content: center;
  padding-top: 12px;
}

// 内容滚动区
.content-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  padding-bottom: 40px;
}

// 提示横幅
.tip-banner {
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  background: linear-gradient(135deg, #EFF6FF 0%, #E8F4FD 100%);
  border-radius: 12px;
  margin-bottom: 14px;
  border: 1px solid rgba(59, 130, 246, 0.1);

  .tip-icon {
    font-size: 18px;
    margin-right: 8px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .tip-text {
    font-size: 13px;
    color: #4B7BB5;
    line-height: 1.6;
  }
}

// 表单卡片
.form-card {
  background: #fff;
  border-radius: 14px;
  padding: 4px 16px 16px;
  margin-bottom: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.form-section {
  padding: 16px 0 12px;
  border-bottom: 1px solid #F0F2F5;

  &:last-child {
    border-bottom: none;
  }
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #17181A;
}

.section-hint {
  font-size: 12px;
  color: #999;
}

// 标签按钮组
.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.tag-btn {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 20px;
  border: 1.5px solid #E5E7EB;
  background: #fff;
  cursor: pointer;
  transition: all 0.25s ease;

  .tag-emoji {
    font-size: 14px;
    margin-right: 4px;
  }

  .tag-label {
    font-size: 13px;
    color: #333;
    white-space: nowrap;
  }
}

.tag-active {
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border-color: #3B82F6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  .tag-label {
    color: #fff;
    font-weight: 500;
  }
}

.tag-disabled {
  opacity: 0.7;
  cursor: default;
  pointer-events: none;
}

// 影响程度标签
.impact-active-green {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  border-color: #10B981;
  .tag-label { color: #fff; font-weight: 500; }
}

.impact-active-orange {
  background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
  border-color: #F59E0B;
  .tag-label { color: #fff; font-weight: 500; }
}

.impact-active-red {
  background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
  border-color: #EF4444;
  .tag-label { color: #fff; font-weight: 500; }
}

// 地点信息
.location-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  background: #F8FAFC;
  border-radius: 10px;
  margin-top: 10px;

  .location-content {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;

    .location-pin {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #3B82F6;
      position: relative;
      margin-right: 10px;
      flex-shrink: 0;

      &::after {
        content: '';
        position: absolute;
        top: 3px;
        left: 3px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-sizing: border-box;
      }
    }

    .location-text {
      font-size: 14px;
      color: #17181A;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .precise-btn {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    background: #EFF6FF;
    border-radius: 16px;
    margin-left: 10px;
    cursor: pointer;
    flex-shrink: 0;

    .precise-icon {
      font-size: 12px;
      margin-right: 4px;
    }

    .precise-text {
      font-size: 12px;
      color: #3B82F6;
      white-space: nowrap;
      font-weight: 500;
    }
  }
}

// 地点-地图区域
.location-map {
  margin-top: 10px;
  cursor: pointer;

  .mapBanner {
    width: 100%;
    position: relative;
    border-radius: 10px;
    overflow: hidden;

    img {
      width: 100%;
      display: block;
    }

    .map-container {
      height: 150px;
    }

    .map-overlay-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 8px 20px;
      background: rgba(255, 165, 0, 0.85);
      color: #fff;
      font-size: 14px;
      font-weight: 500;
      border-radius: 6px;
      white-space: nowrap;
      z-index: 1;
    }
  }

  .map-label {
    font-size: 12px;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 8px;
  }
}

// 时间显示
.time-display {
  display: flex;
  align-items: center;
  padding: 14px;
  background: #F8FAFC;
  border-radius: 10px;
  margin-top: 10px;
  cursor: pointer;

  .time-icon {
    font-size: 16px;
    margin-right: 10px;
  }

  .time-text {
    font-size: 14px;
    color: #17181A;
  }
}

// 车道选择
.lane-picker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  background: #F8FAFC;
  border-radius: 10px;
  margin-top: 10px;
  cursor: pointer;

  .lane-text {
    font-size: 14px;
    color: #17181A;
  }

  .lane-arrow {
    width: 8px;
    height: 8px;
    border-right: 2px solid #999;
    border-bottom: 2px solid #999;
    transform: rotate(45deg);
    margin-left: 8px;
  }
}

// 车道选择弹窗
.lane-popup {
  padding-bottom: env(safe-area-inset-bottom, 20px);

  .lane-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #F0F2F5;

    .lane-popup-cancel { font-size: 14px; color: #999; }
    .lane-popup-title { font-size: 16px; font-weight: 600; color: #17181A; }
    .lane-popup-confirm { font-size: 14px; color: #3B82F6; font-weight: 500; }
  }

  .lane-popup-body {
    padding: 8px 20px 20px;
  }

  .lane-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid #F5F5F5;
    font-size: 15px;
    color: #17181A;
    cursor: pointer;

    &:last-child { border-bottom: none; }
    .lane-check { color: #3B82F6; font-weight: 600; font-size: 18px; }
  }

  .lane-selected { color: #3B82F6; font-weight: 500; }
}

.app-location-picker {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: #fff;
  display: flex;
  flex-direction: column;

  .app-location-header {
    height: 48px;
    padding: env(safe-area-inset-top, 0) 16px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: content-box;
    border-bottom: 1px solid #F0F2F5;
    background: #fff;
  }

  .app-location-title {
    font-size: 17px;
    font-weight: 600;
    color: #17181A;
  }

  .app-location-cancel,
  .app-location-confirm {
    font-size: 14px;
    color: #3B82F6;
    min-width: 44px;
  }

  .app-location-confirm {
    text-align: right;
    font-weight: 500;
  }

  .app-location-tip {
    padding: 10px 16px;
    text-align: center;
    font-size: 13px;
    color: #F59E0B;
    background: #FFF7E6;
  }

  .app-location-map-wrap {
    flex: 1;
    min-height: 0;
    position: relative;
  }

  .app-location-map {
    width: 100%;
    height: 100%;
  }

  .app-location-marker {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 28px;
    height: 28px;
    transform: translate(-50%, -100%);
    z-index: 2;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      width: 24px;
      height: 24px;
      background: #F59E0B;
      border: 3px solid #fff;
      border-radius: 50% 50% 50% 0;
      transform: translateX(-50%) rotate(-45deg);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }
  }

  .app-location-address {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: calc(env(safe-area-inset-bottom, 0) + 18px);
    z-index: 2;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    color: #333;
    font-size: 13px;
    line-height: 1.5;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  }
}

// 媒体附件区
.media-card {
  .media-upload-area {
    display: flex;
    flex-wrap: wrap;
    margin-top: 12px;
  }

  .upload-item {
    display: inline-block;
    width: 80px;
    height: 106px;
    vertical-align: top;
    margin-right: 10px;
    margin-bottom: 16px;

    .upload-item-inner {
      width: 80px;
      height: 80px;
      border-radius: 4px;
      overflow: hidden;
      position: relative;

      img,
      .upload-video-thumb {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        pointer-events: auto;
      }

      .upload-play {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 28px;
        height: 28px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
        pointer-events: none;
      }

      .upload-delete {
        position: absolute;
        top: 4px;
        right: 4px;
        width: 18px;
        height: 18px;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 10px;
        cursor: pointer;
        z-index: 2;
      }
    }

    .upload-label {
      opacity: 0.5;
      font-weight: 500;
      font-size: 12px;
      text-align: center;
      display: block;
      margin-top: 9px;
    }
  }

  .upload-add {
    display: inline-block;
    width: 80px;
    height: 106px;
    vertical-align: top;

    .upload-add-inner {
      width: 80px;
      height: 80px;
      background: rgba(0, 136, 255, 0.1);
      border-radius: 4px;
      position: relative;
      cursor: pointer;
      // APP WebView 兼容点：必须让用户真实手势直接命中处于普通文档流里的 input。
      // 这里对齐 policeReportPage.vue / photo.vue 的线上稳定写法：
      // input 放在图标节点后面，占据自身 80px 高度，再用 top:-80px 视觉上覆盖图标。
      // 不要改成 absolute 或 JS click()，部分 APP WebView 会直接忽略，表现为点击无反应。

      img {
        width: 100%;
        height: 100%;
        border-radius: 4px;
      }

      .video-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: 28px;
      }

      .upload-file-input {
        position: relative;
        left: 0;
        top: -80px;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
        display: block;
      }
    }

    .upload-video-add {
      display: block;
      background: rgba(0, 136, 255, 0.08);
    }

    .upload-label {
      opacity: 0.5;
      font-weight: 500;
      font-size: 12px;
      text-align: center;
      display: block;
      margin-top: 9px;
    }
  }

  .no-media-tip {
    font-size: 13px;
    color: #999;
    text-align: center;
    padding: 20px 0;
  }

  .media-fallback-tip {
    width: 100%;
    font-size: 13px;
    line-height: 1.6;
    color: #8a6d3b;
    background: #fcf8e3;
    border: 1px solid #faebcc;
    border-radius: 6px;
    padding: 10px 12px;
  }
}

// 底部操作
.bottom-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  padding: 0 4px;
}

.submit-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 0;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border-radius: 12px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);

  .submit-icon { margin-right: 6px; font-size: 16px; }
  &.disabled { opacity: 0.6; pointer-events: none; }
}

.draft-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  background: #fff;
  border: 1.5px solid #E5E7EB;
  border-radius: 12px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &.disabled { opacity: 0.6; pointer-events: none; }
}

// 底部提示
.bottom-tip {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-top: 16px;
  line-height: 1.6;
  padding: 0 10px;
}

.safe-bottom {
  height: env(safe-area-inset-bottom, 20px);
}

// TDesign 时间选择器样式覆盖
.newcss {
  font-family: Arial, Helvetica, sans-serif;
  & :deep(.t-picker__title) { font-size: 15px !important; }
  & :deep(.t-picker__cancel) { font-size: 15px !important; }
  & :deep(.t-picker__confirm) { font-size: 15px !important; }
  & :deep(.t-picker-item) {
    margin-top: 20Px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 15px;
    height: var(--td-picker-item-height, 45px);
  }
  & :deep(.t-picker__indicator) {
    top: 2rem;
    height: var(--td-picker-item-height, 45px);
  }
}
.newcss5 {
  font-family: Arial, Helvetica, sans-serif;
  & :deep(.t-picker__title) { font-size: 15px !important; }
  & :deep(.t-picker__cancel) { font-size: 15px !important; }
  & :deep(.t-picker__confirm) { font-size: 15px !important; }
  & :deep(.t-picker-item) {
    margin-top: 6Px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 15px;
    height: var(--td-picker-item-height, 45px);
  }
  & :deep(.t-picker__indicator) {
    top: 2rem;
    height: var(--td-picker-item-height, 45px);
  }
}
.t_date_time_picker_self {
  & :deep(.t-picker__indicator) {
    top: 2rem;
  }
}
</style>
